// src/QC/QC_All_Screens.js
import React, { useCallback, useEffect, useState } from 'react';
import { getStoredRole, getStoredUser, qcApi } from './../api';
import { useMasterData } from '../../utils/useMasterData';
import ModuleGoalsDashboard from '../Goals/ModuleGoalsDashboard';
import {
  LayoutDashboard,
  List,
  CheckSquare,
  RefreshCcw,
  AlertOctagon,
  ShieldCheck,
  History,
  Search,
  Scissors, Hammer, Link, Diamond,
  Bell,
  LogOut,
  ChevronRight,
  Clock,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  BarChart3,
  ArrowUpRight,
  Gem,
  GitMerge,
  Zap,
  ArrowRight,
  SlidersHorizontal,
  ChevronDown,
  ArrowLeft,
  Camera,
  Lock,
  ArrowLeftRight,
  User,
  Download,
  AlertCircle,
  Beaker,
  Droplets,
  Flame,
  Layers,
  Scale,
  UploadCloud
} from 'lucide-react';
import { setAuthToken } from './../api';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, BarChart, Bar, Legend
} from 'recharts';

// --- API Service Functions ---

const QCService = {
  // 1️⃣ QC Dashboard
  getDashboard: async () => {
    try {
      const response = await qcApi.get('/qc/dashboard');
      return response.data;
    } catch (error) {
      console.error('Dashboard fetch error:', error);
      throw error;
    }
  },

  // 2️⃣ QC Queue
  getQueue: async () => {
    try {
      const response = await qcApi.get('/qc/queue');
      return response.data;
    } catch (error) {
      console.error('Queue fetch error:', error);
      throw error;
    }
  },

  // 3️⃣ QC Stage Review
  reviewStage: async (stageId, data) => {
    try {
      const response = await qcApi.post(`/qc/stages/${stageId}/review`, data);
      return response.data;
    } catch (error) {
      console.error('Stage review error:', error);
      throw error;
    }
  },

  uploadInspectionPhotos: async (inspectionId, files = []) => {
    const formData = new FormData();
    files.forEach((file) => formData.append('photos', file));
    const response = await qcApi.post(`/qc/inspections/${inspectionId}/photos`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  getInspectionDetail: async (inspectionId) => {
    const response = await qcApi.get(`/qc/inspections/${inspectionId}`);
    return response.data;
  },

  // 4️⃣ Rework Queue
  getReworks: async () => {
    try {
      const response = await qcApi.get('/qc/reworks');
      return response.data;
    } catch (error) {
      console.error('Reworks fetch error:', error);
      throw error;
    }
  },

  getReworkCostReport: async () => {
    try {
      const response = await qcApi.get('/qc/reworks/cost-report');
      return response.data;
    } catch (error) {
      console.error('Rework cost report fetch error:', error);
      throw error;
    }
  },

  // 5️⃣ Rework Review
  reviewRework: async (reworkId, data) => {
    try {
      const response = await qcApi.post(`/qc/reworks/${reworkId}/review`, data);
      return response.data;
    } catch (error) {
      console.error('Rework review error:', error);
      throw error;
    }
  },

  // 6️⃣ Inventory QC Conflict
  createConflict: async (data) => {
    try {
      const response = await qcApi.post('/qc/inventory-conflicts', data);
      return response.data;
    } catch (error) {
      console.error('Conflict creation error:', error);
      throw error;
    }
  },

  resolveConflict: async (conflictId, data) => {
    try {
      const response = await qcApi.post(`/qc/inventory-conflicts/${conflictId}/resolve`, data);
      return response.data;
    } catch (error) {
      console.error('Conflict resolve error:', error);
      throw error;
    }
  },

  // 7️⃣ Final QC
  getFinalQCQueue: async () => {
    try {
      const response = await qcApi.get('/final-qc/queue');
      return response.data;
    } catch (error) {
      console.error('Final QC queue error:', error);
      throw error;
    }
  },

  submitFinalQC: async (jobCardId, data) => {
    try {
      const response = await qcApi.post(`/qc/job-cards/${jobCardId}/final`, data);
      return response.data;
    } catch (error) {
      console.error('Final QC submit error:', error);
      throw error;
    }
  },
  // 8️⃣ QC History
  getHistory: async ({ page = 1, limit = 100, search = '', from_date, to_date }) => {
    try {
      const response = await qcApi.get('/qc/history', {
        params: { page, limit, search, from_date, to_date }
      });
      return response.data;
    } catch (error) {
      console.error('QC history fetch error:', error);
      throw error;
    }
  },


  // 9️⃣ Export QC History
  exportHistory: async () => {
    const response = await qcApi.get('/qc/history/export', {
      responseType: 'blob'
    });

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'qc-history.csv');
    document.body.appendChild(link);
    link.click();
    link.remove();
  },
  // 🔟 Get QC Review Data (NEW)
  getStageReview: async (stageId) => {
    try {
      const response = await qcApi.get(`/qc/stages/${stageId}/review`);
      return response.data;
    } catch (error) {
      console.error('QC review fetch error:', error);
      throw error;
    }
  },

  returnStageStones: async (stageId, stones = []) => {
    try {
      const response = await qcApi.post(`/job-cards/stages/${stageId}/return-stones`, { stones });
      return response.data;
    } catch (error) {
      console.error('Stage stone return error:', error);
      throw error;
    }
  },

  // Re-Inspection Queue (rework-done items)
  getReinspectionQueue: async () => {
    try {
      const response = await qcApi.get('/qc/reinspection-queue');
      return response.data;
    } catch (error) {
      console.error('Reinspection queue fetch error:', error);
      throw error;
    }
  },

  // 1️⃣1️⃣ Get QC Checklist for a stage code
  getChecklist: async (stageCode) => {
    try {
      const response = await qcApi.get(`/qc/checklists/${stageCode}`);
      return response.data;
    } catch (error) {
      console.error('Checklist fetch error:', error);
      return { stage_code: stageCode, items: ['Visual inspection', 'Weight check', 'Dimensional check'], critical_items: [] };
    }
  },

  // 1️⃣2️⃣ Submit checklist responses
  submitChecklistResponse: async (stageId, data) => {
    try {
      const response = await qcApi.post(`/qc/stages/${stageId}/checklist-response`, data);
      return response.data;
    } catch (error) {
      console.error('Checklist response error:', error);
      throw error;
    }
  },

  // 1️⃣3️⃣ Defect analytics
  getDefectAnalytics: async (days = 30) => {
    try {
      const response = await qcApi.get(`/qc/analytics/defects?days=${days}`);
      return response.data;
    } catch (error) {
      console.error('Defect analytics error:', error);
      throw error;
    }
  },

  getAnalyticsSummary: async () => {
    try {
      const response = await qcApi.get('/qc/analytics/summary');
      return response.data;
    } catch (error) {
      console.error('Analytics summary error:', error);
      throw error;
    }
  },

  getPassFailTrend: async (days = 30) => {
    try {
      const response = await qcApi.get('/qc/analytics/pass-fail-trend', { params: { days } });
      return response.data;
    } catch (error) {
      console.error('Pass/fail trend error:', error);
      throw error;
    }
  },

  getInspectorPerformance: async (days = 30) => {
    try {
      const response = await qcApi.get('/qc/analytics/inspectors', { params: { days } });
      return response.data;
    } catch (error) {
      if (error?.response?.status === 404) {
        const fallback = await qcApi.get('/qc/analytics/inspector-performance', { params: { days } });
        return fallback.data;
      }
      console.error('Inspector performance error:', error);
      throw error;
    }
  },

  getReworkAging: async () => {
    try {
      const response = await qcApi.get('/qc/reworks/aging');
      return response.data;
    } catch (error) {
      if (error?.response?.status === 404) {
        const fallback = await qcApi.get('/qc/analytics/rework-aging');
        return fallback.data;
      }
      console.error('Rework aging error:', error);
      throw error;
    }
  },

  checkSla: async (thresholdMinutes = 240) => {
    try {
      const response = await qcApi.post('/qc/check-sla', { threshold_minutes: thresholdMinutes });
      return response.data;
    } catch (error) {
      if (error?.response?.status === 404) {
        return { success: false, fallback: true, message: 'SLA check endpoint unavailable on current backend' };
      }
      console.error('QC SLA check error:', error);
      throw error;
    }
  },

  getAlerts: async () => {
    try {
      const response = await qcApi.get('/notifications/unread');
      return response.data;
    } catch (error) {
      if (!error?.response || error?.response?.status === 404) {
        const fallback = await qcApi.get('/qc/alerts');
        return fallback.data;
      }
      console.error('QC alerts fetch error:', error);
      throw error;
    }
  },

  markAlertRead: async (alertId) => {
    try {
      const response = await qcApi.put(`/notifications/${alertId}/read`);
      return response.data;
    } catch (error) {
      if (!error?.response || error?.response?.status === 404) {
        const fallback = await qcApi.put(`/qc/alerts/${alertId}/read`);
        return fallback.data;
      }
      console.error('QC alert mark read error:', error);
      throw error;
    }
  },

  getInspectorWorkload: async () => {
    try {
      const response = await qcApi.get('/qc/analytics/inspector-workload');
      return response.data;
    } catch (error) {
      if (error?.response?.status === 404) {
        const queueResponse = await qcApi.get('/qc/queue');
        const queue = Array.isArray(queueResponse.data) ? queueResponse.data : [];
        const grouped = {};
        queue.forEach((item) => {
          const key = item.submitted_by || 'Unassigned';
          if (!grouped[key]) {
            grouped[key] = { inspector_id: null, name: key, pending_count: 0, stages: [] };
          }
          grouped[key].pending_count += 1;
          grouped[key].stages.push({
            stage_id: item.stage_id,
            job_card_no: item.job_card_no,
            stage_name: item.stage_name,
            stage_code: item.stage_name,
            waiting_minutes: item.waiting_minutes || 0,
            assigned_inspector_id: null,
            assigned_inspector_name: key,
          });
        });
        return { success: true, inspectors: [], workload: Object.values(grouped) };
      }
      console.error('Inspector workload error:', error);
      throw error;
    }
  },

  reassignStage: async (stageId, newInspectorId) => {
    try {
      const response = await qcApi.post(`/qc/stages/${stageId}/reassign`, { new_inspector_id: newInspectorId });
      return response.data;
    } catch (error) {
      if (error?.response?.status === 404) {
        return { success: false, fallback: true, message: 'Reassign endpoint unavailable on current backend' };
      }
      console.error('Stage reassign error:', error);
      throw error;
    }
  },

  // 1️⃣4️⃣ Weight variance for a stage
  getWeightVariance: async (stageId) => {
    try {
      const response = await qcApi.get(`/qc/analytics/weight-variance/${stageId}`);
      return response.data;
    } catch (error) {
      console.error('Weight variance error:', error);
      return null;
    }
  },

  getStagePhotos: async (stageId) => {
    try {
      const response = await qcApi.get(`/job-cards/stages/${stageId}/photos`);
      return response.data?.data || [];
    } catch (error) {
      console.error('Stage photo fetch error:', error);
      return [];
    }
  },

  uploadStagePhoto: async (stageId, photoBase64, photoType = 'after', remarks = '') => {
    try {
      const response = await qcApi.post(`/job-cards/stages/${stageId}/photos`, {
        photo_base64: photoBase64,
        photo_type: photoType,
        remarks
      });
      return response.data;
    } catch (error) {
      console.error('Stage photo upload error:', error);
      throw error;
    }
  },

  getBypassEligible: async () => {
    try {
      const response = await qcApi.get('/qc/bypass-eligible');
      return response.data;
    } catch (error) {
      console.error('QC bypass eligible fetch error:', error);
      throw error;
    }
  },

  getBypassRules: async () => {
    try {
      const response = await qcApi.get('/qc/bypass-rules');
      return response.data;
    } catch (error) {
      console.error('QC bypass rules fetch error:', error);
      throw error;
    }
  },

  createBypassRule: async (data) => {
    try {
      const response = await qcApi.post('/qc/bypass-rules', data);
      return response.data;
    } catch (error) {
      console.error('QC bypass rule create error:', error);
      throw error;
    }
  },

  deleteBypassRule: async (ruleId) => {
    try {
      const response = await qcApi.delete(`/qc/bypass-rules/${ruleId}`);
      return response.data;
    } catch (error) {
      console.error('QC bypass rule delete error:', error);
      throw error;
    }
  }
};

const toAbsolutePhotoUrl = (photoUrl) => {
  if (!photoUrl) return '';
  try {
    return new URL(photoUrl, qcApi.defaults.baseURL).toString();
  } catch (error) {
    return photoUrl;
  }
};

const formatCurrency = (value) => `Rs. ${Number(value || 0).toFixed(2)}`;
const formatDateTime = (value) => (value ? new Date(value).toLocaleString() : 'Never');
const isBypassAdminUser = (user) => {
  if (!user) return false;
  if (user.is_admin) return true;
  const role = String(user.role || '').toLowerCase();
  if (['admin', 'org_admin', 'qc_manager', 'supervisor', 'production_supervisor'].includes(role)) {
    return true;
  }
  if (Array.isArray(user.access)) {
    return user.access.some((item) => ['admin', 'org_admin'].includes(String(item?.role || '').toLowerCase()));
  }
  return false;
};

// --- COMPONENTS ---

const SummaryCard = ({ title, count, icon: Icon, color, subText, loading = false }) => (
  <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer">
    <div className="flex justify-between items-start">
      <div>
        <p className="text-slate-500 text-sm font-medium uppercase tracking-wide">{title}</p>
        <h3 className="text-3xl font-bold text-slate-800 mt-2">
          {loading ? <span className="text-slate-300">...</span> : count}
        </h3>
        {subText && <p className={`text-xs mt-2 font-medium ${color.text}`}>{subText}</p>}
      </div>
      <div className={`p-3 rounded-lg ${color.bg} ${color.text}`}>
        <Icon size={24} />
      </div>
    </div>
  </div>
);

const CostStageBar = ({ stage, totalCost, maxCost }) => {
  const width = maxCost > 0 ? Math.max((totalCost / maxCost) * 100, 8) : 0;
  return (
    <div className="flex items-center gap-3 text-sm">
      <div className="w-28 text-slate-600 truncate font-medium">{stage}</div>
      <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full bg-gradient-to-r from-rose-500 to-orange-500" style={{ width: `${width}%` }}></div>
      </div>
      <div className="w-28 text-right font-bold text-rose-700">{formatCurrency(totalCost)}</div>
    </div>
  );
};

// --- WAIT TIME HEATMAP BADGE ---
const WaitBadge = ({ minutes }) => {
  if (minutes < 60) return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-700 border border-emerald-200">
      <Clock size={11} /> {Math.floor(minutes / 60)}h {minutes % 60}m
    </span>
  );
  if (minutes < 360) return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-700 border border-amber-200">
      <Clock size={11} /> {Math.floor(minutes / 60)}h {minutes % 60}m
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-red-100 text-red-700 border border-red-200 animate-pulse">
      <AlertTriangle size={11} /> {Math.floor(minutes / 60)}h {minutes % 60}m
    </span>
  );
};

// --- WEIGHT VARIANCE PANEL ---
const WeightVariancePanel = ({ variance }) => {
  if (!variance) return null;
  const color = variance.alert
    ? 'border-red-300 bg-red-50'
    : variance.within_spec
      ? 'border-emerald-300 bg-emerald-50'
      : 'border-amber-300 bg-amber-50';
  const textColor = variance.alert ? 'text-red-700' : variance.within_spec ? 'text-emerald-700' : 'text-amber-700';
  return (
    <div className={`rounded-lg border ${color} px-4 py-3 flex items-center gap-6 text-sm`}>
      <Scale size={18} className={textColor} />
      <div>
        <div className="text-[10px] text-slate-500 font-bold uppercase">Expected</div>
        <div className="font-mono font-bold text-slate-800">{variance.expected_weight}g</div>
      </div>
      <div>
        <div className="text-[10px] text-slate-500 font-bold uppercase">Actual</div>
        <div className="font-mono font-bold text-slate-800">{variance.actual_weight}g</div>
      </div>
      <div>
        <div className="text-[10px] text-slate-500 font-bold uppercase">Variance</div>
        <div className={`font-mono font-bold ${textColor}`}>{variance.variance_pct}%</div>
      </div>
      <div className={`px-3 py-1 rounded-full text-xs font-bold ${textColor} ${color} border`}>
        {variance.alert ? '⚠ ALERT >5%' : variance.within_spec ? '✓ WITHIN SPEC' : '~ BORDERLINE'}
      </div>
    </div>
  );
};

// --- CHECKLIST PANEL ---
const ChecklistPanel = ({ stageCode, onChecklistChange }) => {
  const [items, setItems] = React.useState([]);
  const [checklistId, setChecklistId] = React.useState(null);
  const [responses, setResponses] = React.useState({}); // {itemId: {passed: bool|null, notes: ''}}
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    if (!stageCode) return;
    QCService.getChecklist(stageCode).then(data => {
      const fetchedItems = data.items || [];
      setItems(fetchedItems);
      setChecklistId(data.checklist_id || null);
      const initial = {};
      fetchedItems.forEach(item => {
        const id = item.id || item;
        initial[id] = { passed: null, notes: '' };
      });
      setResponses(initial);
      setLoading(false);
    });
  }, [stageCode]);

  React.useEffect(() => {
    // Build legacy-compatible data for the parent
    const requiredIds = items.filter(i => i.required).map(i => i.id || i);
    const failedRequired = requiredIds.filter(id => responses[id]?.passed === false);
    const checked = {};
    Object.entries(responses).forEach(([id, r]) => {
      checked[id] = r.passed === true;
    });
    onChecklistChange(checked, requiredIds, {
      responses,
      checklistId,
      failedRequired,
      items,
    });
  }, [checklistId, items, onChecklistChange, responses]);

  const setItemResponse = (itemId, passed) => {
    setResponses(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], passed: prev[itemId]?.passed === passed ? null : passed },
    }));
  };

  const setItemNotes = (itemId, notes) => {
    setResponses(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], notes },
    }));
  };

  if (loading) return (
    <div className="p-4 text-slate-400 text-sm animate-pulse">Loading checklist...</div>
  );

  const passedCount = Object.values(responses).filter(r => r.passed === true).length;
  const failedCount = Object.values(responses).filter(r => r.passed === false).length;
  const failedRequiredItems = items.filter(i => i.required && responses[i.id]?.passed === false);

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="bg-indigo-50 px-6 py-3 border-b border-indigo-100 flex justify-between items-center">
        <h3 className="font-bold text-indigo-800 text-sm flex items-center gap-2">
          <CheckSquare size={16} /> Stage Checklist — {stageCode?.replace(/_/g, ' ')}
        </h3>
        <div className="flex items-center gap-3">
          <span className="text-xs font-bold text-emerald-600">{passedCount} passed</span>
          {failedCount > 0 && <span className="text-xs font-bold text-red-600">{failedCount} failed</span>}
          <span className="text-xs text-slate-400">/ {items.length}</span>
        </div>
      </div>
      <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-2">
        {items.map(item => {
          const id = item.id || item;
          const text = item.text || item;
          const isRequired = item.required;
          const resp = responses[id] || {};
          const isPassed = resp.passed === true;
          const isFailed = resp.passed === false;

          return (
            <div key={id} className={`rounded-lg border transition-colors ${isPassed ? 'bg-emerald-50 border-emerald-200' : isFailed ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'}`}>
              <div className="flex items-center gap-3 p-3">
                <div className="flex gap-1.5">
                  <button
                    type="button"
                    onClick={() => setItemResponse(id, true)}
                    className={`px-2.5 py-1 rounded text-xs font-bold transition-all ${isPassed ? 'bg-emerald-600 text-white shadow-sm' : 'bg-white text-slate-400 border border-slate-200 hover:border-emerald-300 hover:text-emerald-600'}`}
                  >
                    Pass
                  </button>
                  <button
                    type="button"
                    onClick={() => setItemResponse(id, false)}
                    className={`px-2.5 py-1 rounded text-xs font-bold transition-all ${isFailed ? 'bg-red-600 text-white shadow-sm' : 'bg-white text-slate-400 border border-slate-200 hover:border-red-300 hover:text-red-600'}`}
                  >
                    Fail
                  </button>
                </div>
                <span className="text-sm text-slate-700 flex-1">{text}</span>
                {isRequired && (
                  <span className="text-[10px] font-bold text-red-600 bg-red-50 px-1.5 py-0.5 rounded border border-red-200 flex-shrink-0">REQUIRED</span>
                )}
                {item.category && (
                  <span className="text-[10px] font-medium text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded flex-shrink-0">{item.category}</span>
                )}
                {isPassed && <CheckCircle2 size={16} className="text-emerald-500 flex-shrink-0" />}
                {isFailed && <XCircle size={16} className="text-red-500 flex-shrink-0" />}
              </div>
              {isFailed && (
                <div className="px-3 pb-3">
                  <input
                    type="text"
                    placeholder={isRequired ? "Defect notes (required)" : "Defect notes (optional)"}
                    value={resp.notes || ''}
                    onChange={(e) => setItemNotes(id, e.target.value)}
                    className="w-full p-2 text-sm border border-red-200 rounded-md bg-white placeholder-red-300 focus:ring-1 focus:ring-red-400 outline-none"
                  />
                </div>
              )}
            </div>
          );
        })}
        {failedRequiredItems.length > 0 && (
          <div className="mt-2 p-2 bg-red-50 rounded border border-red-200 text-xs text-red-700 flex items-center gap-2">
            <AlertTriangle size={12} /> {failedRequiredItems.length} required item{failedRequiredItems.length !== 1 ? 's' : ''} failed — Full Approve is blocked
          </div>
        )}
      </div>
    </div>
  );
};

// --- MAIN DASHBOARD SCREEN ---

const QCDashboardBypassSection = ({
  eligibleWorkers,
  activeRules,
  loading,
  actionKey,
  bypassError,
  onEnable,
  onDisable
}) => (
  <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
    <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-bold text-slate-800">QC Bypass Eligible Workers</h3>
          <p className="text-xs text-slate-500 mt-1">Last 30 days, minimum 10 completed QC stages, pass rate at least 95%.</p>
        </div>
        <div className="px-3 py-2 rounded-lg bg-blue-50 text-blue-700 text-xs font-bold border border-blue-100">
          Conservative Mode
        </div>
      </div>

      {bypassError && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {bypassError}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase text-slate-500 bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left">Worker Name</th>
              <th className="px-4 py-3 text-left">Stage</th>
              <th className="px-4 py-3 text-left">Pass Rate</th>
              <th className="px-4 py-3 text-left">Stages Completed</th>
              <th className="px-4 py-3 text-left">Eligible?</th>
              <th className="px-4 py-3 text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr>
                <td colSpan="6" className="px-4 py-8 text-center text-slate-400">Loading bypass eligibility...</td>
              </tr>
            ) : eligibleWorkers.length > 0 ? eligibleWorkers.map((row) => {
              const buttonKey = `enable-${row.worker_id}-${row.stage_code}`;
              return (
                <tr key={`${row.worker_id}-${row.stage_code}`}>
                  <td className="px-4 py-3 font-semibold text-slate-800">{row.worker_name}</td>
                  <td className="px-4 py-3 text-slate-600">{row.stage_name}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100 px-2.5 py-1 text-xs font-bold">
                      {Number(row.pass_rate || 0).toFixed(2)}%
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{row.stages_completed}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex rounded-full bg-blue-50 text-blue-700 border border-blue-100 px-2.5 py-1 text-xs font-bold">
                      Yes
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      disabled={!!row.active_rule_id || actionKey === buttonKey}
                      onClick={() => onEnable(row)}
                      className="px-3 py-2 rounded-lg bg-blue-600 text-white text-xs font-bold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {row.active_rule_id ? 'Already Enabled' : actionKey === buttonKey ? 'Enabling...' : 'Enable Bypass'}
                    </button>
                  </td>
                </tr>
              );
            }) : (
              <tr>
                <td colSpan="6" className="px-4 py-8 text-center text-slate-400">No workers currently meet the bypass threshold.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>

    <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-bold text-slate-800">Active QC Bypass Rules</h3>
          <p className="text-xs text-slate-500 mt-1">Rules expire after 30 days by default and require manual renewal.</p>
        </div>
        <div className="px-3 py-2 rounded-lg bg-slate-50 text-slate-700 text-xs font-bold border border-slate-200">
          {activeRules.length} Rules
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase text-slate-500 bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left">Worker</th>
              <th className="px-4 py-3 text-left">Stage</th>
              <th className="px-4 py-3 text-left">Pass Rate When Set</th>
              <th className="px-4 py-3 text-left">Enabled By</th>
              <th className="px-4 py-3 text-left">Expires</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr>
                <td colSpan="7" className="px-4 py-8 text-center text-slate-400">Loading active rules...</td>
              </tr>
            ) : activeRules.length > 0 ? activeRules.map((rule) => {
              const buttonKey = `disable-${rule.id}`;
              return (
                <tr key={rule.id}>
                  <td className="px-4 py-3 font-semibold text-slate-800">{rule.worker_name}</td>
                  <td className="px-4 py-3 text-slate-600">{rule.stage_name}</td>
                  <td className="px-4 py-3 text-slate-600">{Number(rule.historical_pass_rate || 0).toFixed(2)}%</td>
                  <td className="px-4 py-3 text-slate-600">{rule.enabled_by}</td>
                  <td className="px-4 py-3 text-slate-600">{formatDateTime(rule.expires_at)}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold border ${
                      rule.status === 'ACTIVE'
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
                        : 'bg-amber-50 text-amber-700 border-amber-100'
                    }`}>
                      {rule.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      disabled={actionKey === buttonKey}
                      onClick={() => onDisable(rule)}
                      className="px-3 py-2 rounded-lg bg-red-50 text-red-700 border border-red-100 text-xs font-bold hover:bg-red-100 disabled:opacity-50"
                    >
                      {actionKey === buttonKey ? 'Disabling...' : 'Disable'}
                    </button>
                  </td>
                </tr>
              );
            }) : (
              <tr>
                <td colSpan="7" className="px-4 py-8 text-center text-slate-400">No active bypass rules found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

const AGING_COLORS = { '0-1d': '#10b981', '1-3d': '#f59e0b', '3-7d': '#f97316', '7d+': '#ef4444' };
const PIE_COLORS = ['#6366f1', '#f59e0b', '#ef4444', '#10b981', '#3b82f6', '#8b5cf6', '#ec4899'];
const ALERT_SEVERITY_STYLE = {
  LOW: 'bg-sky-50 text-sky-700 border-sky-200',
  MEDIUM: 'bg-amber-50 text-amber-700 border-amber-200',
  HIGH: 'bg-orange-50 text-orange-700 border-orange-200',
  CRITICAL: 'bg-red-100 text-red-900 border-red-300',
};

const SkeletonBox = ({ className = '' }) => (
  <div className={`qc-skeleton rounded ${className}`} />
);

const timeAgo = (isoValue) => {
  if (!isoValue) return '--';
  const ts = new Date(isoValue).getTime();
  if (Number.isNaN(ts)) return '--';
  const diff = Math.max(0, Date.now() - ts);
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

const MaterialConsumptionTable = ({ materials }) => {
  if (!Array.isArray(materials) || materials.length === 0) return null;

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="bg-slate-50 px-6 py-3 border-b border-slate-200">
        <h3 className="font-bold text-slate-700 text-sm flex items-center gap-2">
          <Layers size={16} className="text-indigo-600" /> Material Consumption
        </h3>
      </div>
      <div className="p-6 overflow-x-auto">
        <table className="w-full min-w-[680px] text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-slate-400">Material</th>
              <th className="px-4 py-3 text-right text-[10px] font-black uppercase tracking-widest text-slate-400">Issued</th>
              <th className="px-4 py-3 text-right text-[10px] font-black uppercase tracking-widest text-slate-400">Used</th>
              <th className="px-4 py-3 text-right text-[10px] font-black uppercase tracking-widest text-slate-400">Returned</th>
              <th className="px-4 py-3 text-right text-[10px] font-black uppercase tracking-widest text-slate-400">Wastage</th>
              <th className="px-4 py-3 text-center text-[10px] font-black uppercase tracking-widest text-slate-400">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {materials.map((mat, idx) => (
              <tr key={`${mat.material_id || mat.material_name}-${idx}`} className="hover:bg-slate-50/70">
                <td className="px-4 py-3 font-medium text-slate-800">{mat.material_name || `Material #${mat.material_id}`}</td>
                <td className="px-4 py-3 text-right font-mono text-slate-700">{Number(mat.qty_issued || 0).toFixed(3)} {mat.unit || ''}</td>
                <td className="px-4 py-3 text-right font-mono text-indigo-700">{Number(mat.qty_used || 0).toFixed(3)} {mat.unit || ''}</td>
                <td className="px-4 py-3 text-right font-mono text-slate-600">{Number(mat.qty_returned || 0).toFixed(3)} {mat.unit || ''}</td>
                <td className="px-4 py-3 text-right font-mono text-rose-600">{Number(mat.qty_wastage || 0).toFixed(3)} {mat.unit || ''}</td>
                <td className="px-4 py-3 text-center">
                  {mat.has_consumption_warning ? (
                    <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-1 text-[10px] font-bold text-amber-700">
                      <AlertTriangle size={10} /> Check
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-1 text-[10px] font-bold text-emerald-700">
                      <CheckCircle2 size={10} /> OK
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const StoneConsumptionTable = ({
  stones,
  returnDrafts,
  onReturnDraftChange,
  onReturnStones,
  loading = false,
}) => {
  if (!Array.isArray(stones) || stones.length === 0) return null;

  const hasReturnable = stones.some((stone) => Number(stone.qty_pending || 0) > 0);

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="bg-slate-50 px-6 py-3 border-b border-slate-200 flex items-center justify-between gap-4">
        <div>
          <h3 className="font-bold text-slate-700 text-sm flex items-center gap-2">
            <Gem size={16} className="text-fuchsia-600" /> Stone Usage & Returns
          </h3>
          <p className="mt-1 text-xs text-slate-500">
            Review assigned stones and return only the unused quantity back to inventory.
          </p>
        </div>
        <button
          type="button"
          onClick={onReturnStones}
          disabled={loading || !hasReturnable}
          className="inline-flex items-center gap-2 rounded-lg bg-fuchsia-600 px-3 py-2 text-xs font-bold text-white shadow-sm transition hover:bg-fuchsia-700 disabled:cursor-not-allowed disabled:bg-slate-300"
        >
          {loading ? <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/60 border-t-white" /> : <ArrowLeftRight size={14} />}
          Return Unused Stones
        </button>
      </div>
      <div className="p-6 overflow-x-auto">
        <table className="w-full min-w-[980px] text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-slate-400">Stone</th>
              <th className="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-slate-400">Specs</th>
              <th className="px-4 py-3 text-right text-[10px] font-black uppercase tracking-widest text-slate-400">Issued</th>
              <th className="px-4 py-3 text-right text-[10px] font-black uppercase tracking-widest text-slate-400">Used</th>
              <th className="px-4 py-3 text-right text-[10px] font-black uppercase tracking-widest text-slate-400">Returned</th>
              <th className="px-4 py-3 text-right text-[10px] font-black uppercase tracking-widest text-slate-400">Pending</th>
              <th className="px-4 py-3 text-right text-[10px] font-black uppercase tracking-widest text-slate-400">Return Now</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {stones.map((stone, idx) => {
              const pending = Number(stone.qty_pending || 0);
              const draftKey = String(stone.material_id || `${stone.stone_name || 'stone'}-${stone.size || idx}`);
              const draftValue = returnDrafts[draftKey] ?? '';
              return (
                <tr key={`${draftKey}-${idx}`} className="hover:bg-slate-50/70">
                  <td className="px-4 py-3">
                    <div className="font-semibold text-slate-800">{stone.stone_name || 'Stone'}</div>
                    <div className="text-[11px] text-slate-400">{stone.stone_type || 'Assigned stone'}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-2">
                      {[
                        stone.size ? `Size ${stone.size}` : null,
                        stone.color ? `Color ${stone.color}` : null,
                        stone.quality_grade ? `Grade ${stone.quality_grade}` : null,
                        stone.clarity ? `Clarity ${stone.clarity}` : null,
                      ].filter(Boolean).map((item) => (
                        <span key={item} className="rounded-full border border-slate-200 bg-slate-50 px-2 py-1 text-[10px] font-semibold text-slate-600">
                          {item}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-slate-700">{Number(stone.qty_issued || 0)} {stone.unit || ''}</td>
                  <td className="px-4 py-3 text-right font-mono text-indigo-700">{Number(stone.qty_used || 0)} {stone.unit || ''}</td>
                  <td className="px-4 py-3 text-right font-mono text-emerald-700">{Number(stone.qty_returned || 0)} {stone.unit || ''}</td>
                  <td className="px-4 py-3 text-right font-mono text-amber-700">{pending} {stone.unit || ''}</td>
                  <td className="px-4 py-3 text-right">
                    <input
                      type="number"
                      min="0"
                      max={pending}
                      value={draftValue}
                      onChange={(e) => onReturnDraftChange(stone, idx, e.target.value)}
                      disabled={loading || pending <= 0}
                      className="w-24 rounded-md border border-slate-300 px-2 py-1.5 text-right font-mono text-sm outline-none focus:border-fuchsia-500 focus:ring-2 focus:ring-fuchsia-200 disabled:cursor-not-allowed disabled:bg-slate-100"
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const getStagePanelTheme = (stageCode) => {
  switch (stageCode) {
    case 'WAX_CREATION':
      return { bg: 'bg-orange-50', border: 'border-orange-100', text: 'text-orange-700' };
    case 'WAX_CLEANING':
      return { bg: 'bg-cyan-50', border: 'border-cyan-100', text: 'text-cyan-700' };
    case 'WAX_SETTING':
      return { bg: 'bg-indigo-50', border: 'border-indigo-100', text: 'text-indigo-700' };
    case 'TREE_MAKING':
      return { bg: 'bg-green-50', border: 'border-green-100', text: 'text-green-700' };
    case 'MOLDING':
    case 'CASTING':
      return { bg: 'bg-red-50', border: 'border-red-100', text: 'text-red-700' };
    case 'TREE_CUTTING':
      return { bg: 'bg-amber-50', border: 'border-amber-100', text: 'text-amber-700' };
    case 'FILING':
      return { bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-700' };
    case 'BUFFING':
      return { bg: 'bg-blue-50', border: 'border-blue-100', text: 'text-blue-700' };
    case 'ASSEMBLING':
      return { bg: 'bg-indigo-50', border: 'border-indigo-100', text: 'text-indigo-700' };
    case 'HAND_SETTING':
      return { bg: 'bg-emerald-50', border: 'border-emerald-100', text: 'text-emerald-700' };
    case 'POLISHING':
      return { bg: 'bg-purple-50', border: 'border-purple-100', text: 'text-purple-700' };
    default:
      return { bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-700' };
  }
};

const StageQCDataPanel = ({ stageCode, completionSummary }) => {
  const summary = completionSummary || {};

  if (stageCode === 'WAX_CREATION') {
    const moldsInjected = Number(summary.molds_injected || 0);
    const goodPatterns = Number(summary.good_patterns || 0);
    const rejectedPatterns = Number(summary.rejected_patterns || 0);
    const yieldPct = Number(summary.pattern_yield_pct ?? summary.yield_pct ?? 0);
    const yieldClasses = yieldPct >= 90
      ? 'bg-green-50 text-green-700 border-green-200'
      : yieldPct >= 70
        ? 'bg-orange-50 text-orange-700 border-orange-200'
        : 'bg-red-50 text-red-700 border-red-200';

    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden mt-4">
        <div className="bg-orange-50 px-6 py-3 border-b border-orange-100">
          <h3 className="font-bold text-orange-700 text-sm flex items-center gap-2">
            <AlertTriangle size={16} /> Wax Pattern Metrics
          </h3>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="rounded-lg border bg-slate-50 p-4">
              <div className="text-[10px] text-slate-400 font-bold uppercase">Molds Injected</div>
              <div className="text-lg font-bold font-mono text-slate-800">{moldsInjected}</div>
            </div>
            <div className="rounded-lg border bg-slate-50 p-4">
              <div className="text-[10px] text-slate-400 font-bold uppercase">Good Patterns</div>
              <div className="text-lg font-bold font-mono text-emerald-700">{goodPatterns}</div>
            </div>
            <div className="rounded-lg border bg-slate-50 p-4">
              <div className="text-[10px] text-slate-400 font-bold uppercase">Rejected Patterns</div>
              <div className={`text-lg font-bold font-mono ${rejectedPatterns > 0 ? 'text-red-600' : 'text-slate-800'}`}>{rejectedPatterns}</div>
            </div>
            <div className={`rounded-lg border p-4 ${yieldClasses}`}>
              <div className="text-[10px] font-bold uppercase opacity-80">Pattern Yield %</div>
              <div className="text-lg font-bold font-mono">{yieldPct.toFixed(1)}%</div>
            </div>
          </div>
          {(summary.rejection_reason && summary.rejection_reason !== 'None') && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <div className="text-[10px] text-slate-400 font-bold uppercase">Rejection Reason</div>
              <div className="text-sm font-semibold text-slate-800">{summary.rejection_reason}</div>
            </div>
          )}
          {summary.wax_batch_number && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <div className="text-[10px] text-slate-400 font-bold uppercase">Wax Batch Number</div>
              <div className="text-sm font-semibold text-slate-800">{summary.wax_batch_number}</div>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (stageCode === 'WAX_CLEANING') {
    const received = Number(summary.patterns_received || 0);
    const cleaned = Number(summary.patterns_cleaned || 0);
    const rework = Number(summary.patterns_rework || 0);
    const scrapped = Number(summary.patterns_scrapped || 0);
    const scrapPct = Number(summary.scrap_rate_pct || 0);
    const scrapClasses = scrapPct <= 5
      ? 'bg-green-50 text-green-700 border-green-200'
      : scrapPct <= 15
        ? 'bg-orange-50 text-orange-700 border-orange-200'
        : 'bg-red-50 text-red-700 border-red-200';

    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden mt-4">
        <div className="bg-cyan-50 px-6 py-3 border-b border-cyan-100">
          <h3 className="font-bold text-cyan-700 text-sm flex items-center gap-2">
            <Beaker size={16} /> Wax Cleaning Metrics
          </h3>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="rounded-lg border bg-slate-50 p-4">
              <div className="text-[10px] text-slate-400 font-bold uppercase">Patterns Received</div>
              <div className="text-lg font-bold font-mono text-slate-800">{received}</div>
            </div>
            <div className="rounded-lg border bg-slate-50 p-4">
              <div className="text-[10px] text-slate-400 font-bold uppercase">Patterns Cleaned</div>
              <div className="text-lg font-bold font-mono text-emerald-700">{cleaned}</div>
            </div>
            <div className="rounded-lg border bg-slate-50 p-4">
              <div className="text-[10px] text-slate-400 font-bold uppercase">Patterns Need Rework</div>
              <div className="text-lg font-bold font-mono text-orange-700">{rework}</div>
            </div>
            <div className="rounded-lg border bg-slate-50 p-4">
              <div className="text-[10px] text-slate-400 font-bold uppercase">Patterns Scrapped</div>
              <div className={`text-lg font-bold font-mono ${scrapped > 0 ? 'text-red-600' : 'text-slate-800'}`}>{scrapped}</div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <div className="text-[10px] text-slate-400 font-bold uppercase">Cleaning Method</div>
              <div className="text-sm font-semibold text-slate-800">{summary.cleaning_method || '—'}</div>
            </div>
            <div className={`rounded-lg border p-4 ${scrapClasses}`}>
              <div className="text-[10px] font-bold uppercase opacity-80">Scrap Rate</div>
              <div className="text-lg font-bold font-mono">{scrapPct.toFixed(1)}%</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (stageCode === 'WAX_SETTING') {
    const stonesIssued = Number(summary.stones_issued || 0);
    const stonesSet = Number(summary.stones_set || 0);
    const stonesBroken = Number(summary.stones_broken || 0);
    const stonesReturned = Number(summary.stones_returned || 0);
    const balanceOk = Boolean(summary.stones_balance_ok);
    const patternsWithStones = Number(summary.patterns_with_stones || 0);

    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden mt-4">
        <div className="bg-indigo-50 px-6 py-3 border-b border-indigo-100">
          <h3 className="font-bold text-indigo-700 text-sm flex items-center gap-2">
            <Gem size={16} /> Stone Setting Metrics
          </h3>
        </div>
        <div className="p-6 space-y-4">
          {!balanceOk && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-700">
              ALERT: Stone accountability mismatch. Issued stones do not balance against set, broken, and returned.
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="rounded-lg border bg-slate-50 p-4">
              <div className="text-[10px] text-slate-400 font-bold uppercase">Stones Issued</div>
              <div className="text-lg font-bold font-mono text-slate-800">{stonesIssued}</div>
            </div>
            <div className="rounded-lg border bg-slate-50 p-4">
              <div className="text-[10px] text-slate-400 font-bold uppercase">Stones Set</div>
              <div className="text-lg font-bold font-mono text-emerald-700">{stonesSet}</div>
            </div>
            <div className="rounded-lg border bg-slate-50 p-4">
              <div className="text-[10px] text-slate-400 font-bold uppercase">Stones Broken</div>
              <div className="text-lg font-bold font-mono text-red-600">{stonesBroken}</div>
            </div>
            <div className="rounded-lg border bg-slate-50 p-4">
              <div className="text-[10px] text-slate-400 font-bold uppercase">Stones Returned</div>
              <div className="text-lg font-bold font-mono text-blue-700">{stonesReturned}</div>
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <div className="text-[10px] text-slate-400 font-bold uppercase">Stone Accountability</div>
            <div className="mt-1 text-sm font-semibold text-slate-800">
              {stonesIssued} = {stonesSet} + {stonesBroken} + {stonesReturned}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <div className="text-[10px] text-slate-400 font-bold uppercase">Patterns With Stones</div>
              <div className="text-lg font-bold font-mono text-slate-800">{patternsWithStones}</div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <div className="text-[10px] text-slate-400 font-bold uppercase">Setting Quality</div>
              <div className="text-sm font-semibold text-slate-800">{summary.setting_quality || '—'}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (stageCode === 'TREE_MAKING') {
    const patternsAttached = Number(summary.patterns_attached || summary.piece_count || 0);
    const treeWeight = Number(summary.tree_total_weight || summary.tree_weight || 0);
    const sprueWeight = Number(summary.sprue_weight || 0);
    const expectedMetal = Number(summary.expected_metal_g || 0);

    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden mt-4">
        <div className="bg-green-50 px-6 py-3 border-b border-green-100">
          <h3 className="font-bold text-green-700 text-sm flex items-center gap-2">
            <GitMerge size={16} /> Tree Details
          </h3>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="rounded-lg border bg-slate-50 p-4">
              <div className="text-[10px] text-slate-400 font-bold uppercase">Patterns Attached</div>
              <div className="text-lg font-bold font-mono text-slate-800">{patternsAttached}</div>
            </div>
            <div className="rounded-lg border bg-slate-50 p-4">
              <div className="text-[10px] text-slate-400 font-bold uppercase">Tree Total Weight</div>
              <div className="text-lg font-bold font-mono text-slate-800">{treeWeight.toFixed(3)}g</div>
            </div>
            <div className="rounded-lg border bg-slate-50 p-4">
              <div className="text-[10px] text-slate-400 font-bold uppercase">Sprue Weight</div>
              <div className="text-lg font-bold font-mono text-slate-800">{sprueWeight.toFixed(3)}g</div>
            </div>
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
              <div className="text-[10px] text-blue-600 font-bold uppercase">Expected Metal</div>
              <div className="text-lg font-bold font-mono text-blue-700">{expectedMetal.toFixed(2)}g</div>
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <div className="text-[10px] text-slate-400 font-bold uppercase">Flask ID</div>
            <div className="text-sm font-semibold text-slate-800">{summary.flask_id || '—'}</div>
          </div>
        </div>
      </div>
    );
  }

  if (stageCode === 'MOLDING' || stageCode === 'CASTING') {
    const charged = Number(summary.metal_charged || 0);
    const grossOut = Number(summary.gross_weight_after || summary.gross_weight || 0);
    const flaskTemp = Number(summary.flask_temperature || summary.flask_temp || 0);
    const pouringTemp = Number(summary.pouring_temperature || 0);
    const loss = Number(summary.metal_loss || 0);
    const lossPct = Number(summary.metal_loss_pct || 0);
    const defects = summary.casting_defects || 'None';
    const defectList = Array.isArray(defects) ? defects : String(defects).split(',').map((d) => d.trim()).filter(Boolean);
    const lossClasses = lossPct <= 4
      ? 'bg-green-50 text-green-700 border-green-200'
      : lossPct <= 6
        ? 'bg-orange-50 text-orange-700 border-orange-200'
        : 'bg-red-50 text-red-700 border-red-200';
    const flaskAlert = flaskTemp > 0 && (flaskTemp < 550 || flaskTemp > 750);
    const pouringAlert = pouringTemp > 0 && (pouringTemp < 850 || pouringTemp > 1150);

    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden mt-4">
        <div className="bg-red-50 px-6 py-3 border-b border-red-100">
          <h3 className="font-bold text-red-700 text-sm flex items-center gap-2">
            <Flame size={16} /> Casting Parameters
          </h3>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="rounded-lg border bg-slate-50 p-4">
              <div className="text-[10px] text-slate-400 font-bold uppercase">Metal Charged</div>
              <div className="text-lg font-bold font-mono text-slate-800">{charged.toFixed(3)}g</div>
            </div>
            <div className="rounded-lg border bg-slate-50 p-4">
              <div className="text-[10px] text-slate-400 font-bold uppercase">Gross Output</div>
              <div className="text-lg font-bold font-mono text-slate-800">{grossOut.toFixed(3)}g</div>
            </div>
            <div className={`rounded-lg border p-4 ${lossClasses}`}>
              <div className="text-[10px] font-bold uppercase opacity-80">Metal Loss</div>
              <div className="text-lg font-bold font-mono">{loss.toFixed(3)}g</div>
              <div className="text-xs font-semibold">{lossPct.toFixed(2)}%</div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className={`rounded-lg border p-4 ${flaskAlert ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'}`}>
              <div className="text-[10px] text-slate-400 font-bold uppercase">Flask Temperature</div>
              <div className={`text-lg font-bold font-mono ${flaskAlert ? 'text-red-700' : 'text-slate-800'}`}>{flaskTemp.toFixed(0)} C</div>
            </div>
            <div className={`rounded-lg border p-4 ${pouringAlert ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'}`}>
              <div className="text-[10px] text-slate-400 font-bold uppercase">Pouring Temperature</div>
              <div className={`text-lg font-bold font-mono ${pouringAlert ? 'text-red-700' : 'text-slate-800'}`}>{pouringTemp.toFixed(0)} C</div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <div className="text-[10px] text-slate-400 font-bold uppercase">Metal & Method</div>
              <div className="text-sm font-semibold text-slate-800">{summary.metal_purity || '—'}</div>
              <div className="text-sm text-slate-600">{summary.casting_method || '—'}</div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <div className="text-[10px] text-slate-400 font-bold uppercase">Casting Defects</div>
              <div className="mt-2 flex flex-wrap gap-2">
                {defectList.map((defect) => (
                  <span key={defect} className={`inline-flex rounded-full border px-2 py-1 text-[10px] font-bold ${defect === 'None' ? 'border-green-200 bg-green-50 text-green-700' : 'border-red-200 bg-red-50 text-red-700'}`}>
                    {defect}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (stageCode === 'TREE_CUTTING') {
    const totalPiecesCut = Number(summary.total_pieces_cut || 0);
    const goodPieces = Number(summary.good_pieces || 0);
    const defectivePieces = Number(summary.defective_pieces || 0);
    const sprueScrapWeight = Number(summary.sprue_scrap_weight || 0);
    const cuttingLoss = Number(summary.cutting_loss || 0);
    const totalPiecesWeight = Number(summary.total_pieces_weight || summary.pieces_weight || 0);

    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden mt-4">
        <div className="bg-amber-50 px-6 py-3 border-b border-amber-100">
          <h3 className="font-bold text-amber-700 text-sm flex items-center gap-2">
            <Scissors size={16} /> Cutting Metrics
          </h3>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="rounded-lg border bg-slate-50 p-4">
              <div className="text-[10px] text-slate-400 font-bold uppercase">Total Pieces Cut</div>
              <div className="text-lg font-bold font-mono text-slate-800">{totalPiecesCut}</div>
            </div>
            <div className="rounded-lg border bg-slate-50 p-4">
              <div className="text-[10px] text-slate-400 font-bold uppercase">Good Pieces</div>
              <div className="text-lg font-bold font-mono text-emerald-700">{goodPieces}</div>
            </div>
            <div className="rounded-lg border bg-slate-50 p-4">
              <div className="text-[10px] text-slate-400 font-bold uppercase">Defective Pieces</div>
              <div className={`text-lg font-bold font-mono ${defectivePieces > 0 ? 'text-red-600' : 'text-slate-800'}`}>{defectivePieces}</div>
            </div>
            <div className={`rounded-lg border p-4 ${cuttingLoss <= 0.5 ? 'bg-green-50 text-green-700 border-green-200' : cuttingLoss <= 1.5 ? 'bg-orange-50 text-orange-700 border-orange-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
              <div className="text-[10px] font-bold uppercase opacity-80">Cutting Loss</div>
              <div className="text-lg font-bold font-mono">{cuttingLoss.toFixed(3)}g</div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <div className="text-[10px] text-slate-400 font-bold uppercase">Total Pieces Weight / Sprue Scrap</div>
              <div className="text-sm font-semibold text-slate-800">{totalPiecesWeight.toFixed(3)}g / {sprueScrapWeight.toFixed(3)}g</div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <div className="text-[10px] text-slate-400 font-bold uppercase">Defect Types / Scrap Return</div>
              <div className="text-sm font-semibold text-slate-800">{summary.defect_types_found || '—'}</div>
              <div className="text-sm text-slate-600">{summary.scrap_returned_to_pool ? 'Returned to remelt pool' : 'Not returned to pool'}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (stageCode === 'FILING') {
    const piecesReceived = Number(summary.pieces_received || 0);
    const dustCollected = Number(summary.dust_collected || summary.dust_recovery || 0);
    const unrecoverableLoss = Number(summary.unrecoverable_loss || 0);
    const piecesCompleted = Number(summary.pieces_completed || 0);

    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden mt-4">
        <div className="bg-slate-50 px-6 py-3 border-b border-slate-200">
          <h3 className="font-bold text-slate-700 text-sm flex items-center gap-2">
            <Hammer size={16} /> Filing Metrics
          </h3>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="rounded-lg border bg-slate-50 p-4">
              <div className="text-[10px] text-slate-400 font-bold uppercase">Pieces Received</div>
              <div className="text-lg font-bold font-mono text-slate-800">{piecesReceived}</div>
            </div>
            <div className="rounded-lg border bg-slate-50 p-4">
              <div className="text-[10px] text-slate-400 font-bold uppercase">Dust Collected</div>
              <div className="text-lg font-bold font-mono text-slate-800">{dustCollected.toFixed(3)}g</div>
            </div>
            <div className={`rounded-lg border p-4 ${unrecoverableLoss <= 0.2 ? 'bg-green-50 text-green-700 border-green-200' : unrecoverableLoss <= 0.8 ? 'bg-orange-50 text-orange-700 border-orange-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
              <div className="text-[10px] font-bold uppercase opacity-80">Unrecoverable Loss</div>
              <div className="text-lg font-bold font-mono">{unrecoverableLoss.toFixed(3)}g</div>
            </div>
            <div className="rounded-lg border bg-slate-50 p-4">
              <div className="text-[10px] text-slate-400 font-bold uppercase">Pieces Completed</div>
              <div className="text-lg font-bold font-mono text-emerald-700">{piecesCompleted}</div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <div className="text-[10px] text-slate-400 font-bold uppercase">Filing Method</div>
              <div className="text-sm font-semibold text-slate-800">{summary.filing_method || '—'}</div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <div className="text-[10px] text-slate-400 font-bold uppercase">Surface Readiness</div>
              <div className="text-sm font-semibold text-slate-800">{summary.surface_readiness || '—'}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (stageCode === 'BUFFING') {
    const piecesReceived = Number(summary.pieces_received || 0);
    const piecesPassed = Number(summary.pieces_passed || 0);
    const piecesRebuff = Number(summary.pieces_rebuff || 0);

    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden mt-4">
        <div className="bg-blue-50 px-6 py-3 border-b border-blue-100">
          <h3 className="font-bold text-blue-700 text-sm flex items-center gap-2">
            <Zap size={16} /> Surface Quality
          </h3>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="rounded-lg border bg-slate-50 p-4">
              <div className="text-[10px] text-slate-400 font-bold uppercase">Pieces Received</div>
              <div className="text-lg font-bold font-mono text-slate-800">{piecesReceived}</div>
            </div>
            <div className="rounded-lg border bg-slate-50 p-4">
              <div className="text-[10px] text-slate-400 font-bold uppercase">Surface Finish</div>
              <div className="text-sm font-semibold text-slate-800">{summary.surface_finish || '—'}</div>
            </div>
            <div className="rounded-lg border bg-slate-50 p-4">
              <div className="text-[10px] text-slate-400 font-bold uppercase">Buffing Compound</div>
              <div className="text-sm font-semibold text-slate-800">{summary.buffing_compound || '—'}</div>
            </div>
            <div className={`rounded-lg border p-4 ${piecesRebuff === 0 ? 'bg-green-50 text-green-700 border-green-200' : 'bg-orange-50 text-orange-700 border-orange-200'}`}>
              <div className="text-[10px] font-bold uppercase opacity-80">Pieces Rebuff</div>
              <div className="text-lg font-bold font-mono">{piecesRebuff}</div>
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <div className="text-[10px] text-slate-400 font-bold uppercase">Pieces Passed</div>
            <div className="text-lg font-bold font-mono text-emerald-700">{piecesPassed}</div>
          </div>
        </div>
      </div>
    );
  }

  if (stageCode === 'ASSEMBLING') {
    const piecesReceived = Number(summary.pieces_received || 0);
    const solderJointsCount = Number(summary.solder_joints_count || 0);
    const solderWeightUsed = Number(summary.solder_weight_used || 0);
    const piecesAssembled = Number(summary.pieces_assembled || 0);

    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden mt-4">
        <div className="bg-indigo-50 px-6 py-3 border-b border-indigo-100">
          <h3 className="font-bold text-indigo-700 text-sm flex items-center gap-2">
            <Link size={16} /> Joinery Check
          </h3>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="rounded-lg border bg-slate-50 p-4">
              <div className="text-[10px] text-slate-400 font-bold uppercase">Pieces Received</div>
              <div className="text-lg font-bold font-mono text-slate-800">{piecesReceived}</div>
            </div>
            <div className="rounded-lg border bg-slate-50 p-4">
              <div className="text-[10px] text-slate-400 font-bold uppercase">Solder Joints Count</div>
              <div className="text-lg font-bold font-mono text-slate-800">{solderJointsCount}</div>
            </div>
            <div className="rounded-lg border bg-slate-50 p-4">
              <div className="text-[10px] text-slate-400 font-bold uppercase">Solder Type / Weight</div>
              <div className="text-sm font-semibold text-slate-800">{summary.solder_type || '—'}</div>
              <div className="text-sm text-slate-600">{solderWeightUsed.toFixed(3)}g</div>
            </div>
            <div className="rounded-lg border bg-slate-50 p-4">
              <div className="text-[10px] text-slate-400 font-bold uppercase">Pieces Assembled</div>
              <div className="text-lg font-bold font-mono text-emerald-700">{piecesAssembled}</div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <div className="text-[10px] text-slate-400 font-bold uppercase">Alignment Check</div>
              <div className="text-sm font-semibold text-slate-800">{summary.alignment_check || '—'}</div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <div className="text-[10px] text-slate-400 font-bold uppercase">Functional Test</div>
              <div className="text-sm font-semibold text-slate-800">{summary.functional_test || '—'}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (stageCode === 'HAND_SETTING') {
    const stonesIssued = Number(summary.stones_issued || 0);
    const stonesSet = Number(summary.stones_set || 0);
    const stonesBroken = Number(summary.stones_broken || 0);
    const stonesReturned = Number(summary.stones_returned || 0);
    const balanceOk = summary.stones_balance_ok !== false;
    const weightGain = Number(summary.weight_gain || summary.metal_weight_gain || 0);

    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden mt-4">
        <div className="bg-emerald-50 px-6 py-3 border-b border-emerald-100">
          <h3 className="font-bold text-emerald-700 text-sm flex items-center gap-2">
            <Diamond size={16} /> Stone Inventory
          </h3>
        </div>
        <div className="p-6 space-y-4">
          {!balanceOk && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-700">
              ALERT: Stone accountability mismatch. Issued stones do not balance against set, broken, and returned.
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="rounded-lg border bg-slate-50 p-4">
              <div className="text-[10px] text-slate-400 font-bold uppercase">Stones Issued</div>
              <div className="text-lg font-bold font-mono text-slate-800">{stonesIssued}</div>
            </div>
            <div className="rounded-lg border bg-slate-50 p-4">
              <div className="text-[10px] text-slate-400 font-bold uppercase">Stones Set</div>
              <div className="text-lg font-bold font-mono text-emerald-700">{stonesSet}</div>
            </div>
            <div className="rounded-lg border bg-slate-50 p-4">
              <div className="text-[10px] text-slate-400 font-bold uppercase">Stones Broken</div>
              <div className="text-lg font-bold font-mono text-red-600">{stonesBroken}</div>
            </div>
            <div className="rounded-lg border bg-slate-50 p-4">
              <div className="text-[10px] text-slate-400 font-bold uppercase">Stones Returned</div>
              <div className="text-lg font-bold font-mono text-blue-700">{stonesReturned}</div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <div className="text-[10px] text-slate-400 font-bold uppercase">Setting Type / Tightness</div>
              <div className="text-sm font-semibold text-slate-800">{summary.setting_type || '—'}</div>
              <div className="text-sm text-slate-600">{summary.tightness_check || '—'}</div>
            </div>
            <div className={`rounded-lg border p-4 ${weightGain >= 0 ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
              <div className="text-[10px] font-bold uppercase opacity-80">Weight Gain</div>
              <div className="text-lg font-bold font-mono">{weightGain.toFixed(3)}g</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (stageCode === 'POLISHING') {
    const platingApplied = Boolean(summary.plating_applied);
    const platingThickness = Number(summary.plating_thickness_micron || 0);

    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden mt-4">
        <div className="bg-purple-50 px-6 py-3 border-b border-purple-100">
          <h3 className="font-bold text-purple-700 text-sm flex items-center gap-2">
            <Droplets size={16} /> Plating Specs
          </h3>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="rounded-lg border bg-slate-50 p-4">
              <div className="text-[10px] text-slate-400 font-bold uppercase">Polish Type</div>
              <div className="text-sm font-semibold text-slate-800">{summary.polish_type || '—'}</div>
            </div>
            <div className="rounded-lg border bg-slate-50 p-4">
              <div className="text-[10px] text-slate-400 font-bold uppercase">Plating Applied</div>
              <div className="text-sm font-semibold text-slate-800">{platingApplied ? 'Yes' : 'No'}</div>
            </div>
            <div className="rounded-lg border bg-slate-50 p-4">
              <div className="text-[10px] text-slate-400 font-bold uppercase">Final Visual Inspection</div>
              <div className="text-sm font-semibold text-slate-800">{summary.final_visual_inspection || '—'}</div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <div className="text-[10px] text-slate-400 font-bold uppercase">Plating Type</div>
              <div className="text-sm font-semibold text-slate-800">{summary.plating_type || '—'}</div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <div className="text-[10px] text-slate-400 font-bold uppercase">Plating Thickness</div>
              <div className="text-sm font-semibold text-slate-800">{platingApplied ? `${platingThickness.toFixed(2)} micron` : '—'}</div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <div className="text-[10px] text-slate-400 font-bold uppercase">Solution Batch</div>
              <div className="text-sm font-semibold text-slate-800">{summary.plating_solution_batch || '—'}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

const QCDashboard = ({ onNavigate, user }) => {
  const canManageBypass = isBypassAdminUser(user || getStoredUser());

  const [dateRange, setDateRange] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Data state
  const [summary, setSummary] = useState(null);
  const [dashboardData, setDashboardData] = useState({ summary: {}, stage_distribution: [] });
  const [trendData, setTrendData] = useState([]);
  const [defectData, setDefectData] = useState({ by_category: [], by_stage: [] });
  const [agingData, setAgingData] = useState({ total_open: 0, buckets: [] });
  const [inspectorData, setInspectorData] = useState([]);
  const [queueData, setQueueData] = useState([]);
  const [reworkCostReport, setReworkCostReport] = useState({ summary: {}, by_stage: [] });
  const [alerts, setAlerts] = useState([]);
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [alertLoading, setAlertLoading] = useState(false);
  const [inspectorWorkload, setInspectorWorkload] = useState([]);
  const [availableInspectors, setAvailableInspectors] = useState([]);
  const [reassigningStageId, setReassigningStageId] = useState(null);

  // Bypass state
  const [eligibleWorkers, setEligibleWorkers] = useState([]);
  const [activeBypassRules, setActiveBypassRules] = useState([]);
  const [bypassError, setBypassError] = useState(null);
  const [bypassActionKey, setBypassActionKey] = useState('');

  const fetchAll = useCallback(async (days) => {
    setLoading(true);
    setError(null);
    try {
      const results = await Promise.allSettled([
        QCService.getAnalyticsSummary(),
        QCService.getPassFailTrend(days),
        QCService.getDefectAnalytics(days),
        QCService.getReworkAging(),
        QCService.getInspectorPerformance(days),
        QCService.getQueue(),
        QCService.getDashboard(),
        QCService.getReworkCostReport(),
        QCService.getAlerts(),
        QCService.getInspectorWorkload(),
      ]);

      const val = (i) => results[i].status === 'fulfilled' ? results[i].value : null;

      if (val(0)) setSummary(val(0));
      if (val(1)?.data) setTrendData(val(1).data);
      if (val(2)) setDefectData(val(2));
      if (val(3)) setAgingData(val(3));
      if (val(4)) {
        const inspectorRows = Array.isArray(val(4)) ? val(4) : (Array.isArray(val(4).data) ? val(4).data : []);
        setInspectorData(inspectorRows);
      }
      if (val(5)) setQueueData(Array.isArray(val(5)) ? val(5) : []);
      if (val(6)) setDashboardData(val(6));
      if (val(7)) setReworkCostReport(val(7));
      if (val(8)) setAlerts(Array.isArray(val(8).data) ? val(8).data : []);
      if (val(9)) {
        setInspectorWorkload(Array.isArray(val(9).workload) ? val(9).workload : []);
        setAvailableInspectors(Array.isArray(val(9).inspectors) ? val(9).inspectors : []);
      }

      if (canManageBypass) {
        try {
          const [eligible, rules] = await Promise.all([
            QCService.getBypassEligible(),
            QCService.getBypassRules()
          ]);
          setEligibleWorkers(eligible || []);
          setActiveBypassRules(rules || []);
        } catch (e) {
          console.error('Bypass data error:', e);
        }
      }
    } catch (err) {
      console.error('Dashboard fetch error:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [canManageBypass]);

  useEffect(() => { fetchAll(dateRange); }, [dateRange, fetchAll]);
  useEffect(() => {
    const intervalId = setInterval(() => {
      refreshAlerts();
    }, 60000);
    return () => clearInterval(intervalId);
  }, []);

  const handleEnableBypass = async (row) => {
    const actionKey = `enable-${row.worker_id}-${row.stage_code}`;
    try {
      setBypassActionKey(actionKey);
      setBypassError(null);
      await QCService.createBypassRule({ worker_id: row.worker_id, stage_code: row.stage_code });
      const [eligible, rules] = await Promise.all([QCService.getBypassEligible(), QCService.getBypassRules()]);
      setEligibleWorkers(eligible || []);
      setActiveBypassRules(rules || []);
    } catch (err) {
      setBypassError(err.response?.data?.error || 'Failed to enable QC bypass');
    } finally { setBypassActionKey(''); }
  };

  const handleDisableBypass = async (rule) => {
    const actionKey = `disable-${rule.id}`;
    try {
      setBypassActionKey(actionKey);
      setBypassError(null);
      await QCService.deleteBypassRule(rule.id);
      const [eligible, rules] = await Promise.all([QCService.getBypassEligible(), QCService.getBypassRules()]);
      setEligibleWorkers(eligible || []);
      setActiveBypassRules(rules || []);
    } catch (err) {
      setBypassError(err.response?.data?.error || 'Failed to disable QC bypass');
    } finally { setBypassActionKey(''); }
  };

  const refreshAlerts = async () => {
    try {
      setAlertLoading(true);
      const response = await QCService.getAlerts();
      setAlerts(Array.isArray(response?.data) ? response.data : []);
    } catch (err) {
      console.error('Failed to refresh alerts:', err);
    } finally {
      setAlertLoading(false);
    }
  };

  const handleMarkAlertRead = async (alertId) => {
    try {
      await QCService.markAlertRead(alertId);
      setAlerts((prev) => prev.filter((row) => row.id !== alertId));
    } catch (err) {
      console.error('Failed to mark alert as read:', err);
    }
  };

  const handleRunSlaCheck = async () => {
    try {
      await QCService.checkSla(240);
      await refreshAlerts();
    } catch (err) {
      console.error('Failed to run SLA check:', err);
    }
  };

  const handleReassignStage = async (stageId, newInspectorId) => {
    if (!newInspectorId) return;
    try {
      setReassigningStageId(stageId);
      await QCService.reassignStage(stageId, Number(newInspectorId));
      const workload = await QCService.getInspectorWorkload();
      setInspectorWorkload(Array.isArray(workload?.workload) ? workload.workload : []);
      setAvailableInspectors(Array.isArray(workload?.inspectors) ? workload.inspectors : []);
      const queue = await QCService.getQueue();
      setQueueData(Array.isArray(queue) ? queue : []);
    } catch (err) {
      console.error('Failed to reassign stage:', err);
    } finally {
      setReassigningStageId(null);
    }
  };

  // Computed
  const s = summary || {};
  const passRate = s.pass_rate_today ?? 0;
  const escalatedReworks = (agingData?.aging || []).find((row) => row.bucket === '7d+')?.items || [];

  return (
    <div className="flex flex-col h-full bg-slate-50/50 overflow-hidden text-black">
      {/* HEADER */}
      <div className="px-8 py-5 border-b border-slate-200 bg-white flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
            <ShieldCheck className="text-emerald-600" /> QC Control Tower
          </h1>
          <p className="text-slate-500 mt-1 text-sm">Real-time Quality Control Operations</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Date Range Filter */}
          <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
            {[7, 30, 90].map(d => (
              <button
                key={d}
                onClick={() => setDateRange(d)}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${dateRange === d ? 'bg-white text-indigo-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              >
                {d}d
              </button>
            ))}
          </div>
          <button onClick={() => fetchAll(dateRange)} className="p-2 hover:bg-slate-100 rounded-lg text-slate-400">
            <RefreshCcw size={16} />
          </button>
          <button
            onClick={handleRunSlaCheck}
            className="px-3 py-2 bg-amber-50 text-amber-700 rounded-lg text-xs font-semibold border border-amber-100 hover:bg-amber-100"
          >
            Check SLA
          </button>
          <button
            onClick={() => { setAlertsOpen((prev) => !prev); if (!alertsOpen) refreshAlerts(); }}
            className="relative p-2 hover:bg-slate-100 rounded-lg text-slate-500"
            title="QC Alerts"
          >
            <Bell size={18} />
            {alerts.length > 0 && (
              <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                {alerts.length > 99 ? '99+' : alerts.length}
              </span>
            )}
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-8">
        <div className="max-w-7xl mx-auto space-y-6">
          {alertsOpen && (
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                  <Bell size={15} className="text-amber-500" /> SLA Alerts ({alerts.length})
                </h3>
                <button onClick={refreshAlerts} className="text-xs text-indigo-600 font-semibold hover:text-indigo-800">
                  Refresh
                </button>
              </div>
              <div className="max-h-72 overflow-y-auto divide-y divide-slate-100">
                {alertLoading ? (
                  <div className="p-4 space-y-2">
                    {Array.from({ length: 3 }).map((_, i) => <SkeletonBox key={i} className="h-10 w-full" />)}
                  </div>
                ) : alerts.length > 0 ? (
                  alerts.map((alert) => (
                    <div key={alert.id} className="px-4 py-3 flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm text-slate-800 font-medium">{alert.message}</div>
                        <div className="mt-1 flex items-center gap-2">
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${ALERT_SEVERITY_STYLE[alert.severity] || ALERT_SEVERITY_STYLE.MEDIUM}`}>
                            {alert.severity || 'MEDIUM'}
                          </span>
                          <span className="text-xs text-slate-500">{timeAgo(alert.created_at)}</span>
                        </div>
                      </div>
                      <button
                        onClick={() => handleMarkAlertRead(alert.id)}
                        className="text-xs px-2.5 py-1 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold whitespace-nowrap"
                      >
                        Mark Read
                      </button>
                    </div>
                  ))
                ) : (
                  <div className="py-8 text-center text-slate-400 text-sm">No unread QC SLA alerts</div>
                )}
              </div>
            </div>
          )}

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
              <AlertCircle size={16} /> {error}
            </div>
          )}

          {/* KPI CARDS */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <SummaryCard title="Today's Inspections" count={s.total_inspections_today ?? dashboardData.summary?.approved_today ?? 0} icon={CheckSquare} color={{ bg: 'bg-blue-100', text: 'text-blue-600' }} subText={`${s.total_this_month ?? 0} this month`} loading={loading} />
            <SummaryCard title="Pass Rate Today" count={`${passRate}%`} icon={CheckCircle2} color={{ bg: passRate >= 90 ? 'bg-emerald-100' : passRate >= 70 ? 'bg-amber-100' : 'bg-red-100', text: passRate >= 90 ? 'text-emerald-600' : passRate >= 70 ? 'text-amber-600' : 'text-red-600' }} subText={passRate >= 90 ? 'Excellent' : passRate >= 70 ? 'Needs attention' : 'Critical'} loading={loading} />
            <SummaryCard title="Open Reworks" count={s.open_reworks ?? dashboardData.summary?.rework_pending ?? 0} icon={RefreshCcw} color={{ bg: 'bg-orange-100', text: 'text-orange-600' }} subText="Pending resolution" loading={loading} />
            <SummaryCard title="Avg Review Time" count={s.avg_review_minutes ? `${s.avg_review_minutes}m` : '--'} icon={Clock} color={{ bg: 'bg-purple-100', text: 'text-purple-600' }} subText="Minutes per review" loading={loading} />
          </div>

          {/* CHARTS — 2x2 Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* 1. Line Chart — Daily Pass Rate */}
            <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
              <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2 text-sm">
                <BarChart3 size={16} className="text-indigo-600" /> Daily Pass Rate — Last {dateRange} Days
              </h3>
              {loading ? (
                <SkeletonBox className="h-56 w-full" />
              ) : trendData.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d?.slice(5)} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}%`} />
                    <Tooltip formatter={(v) => [`${v}%`, 'Pass Rate']} labelFormatter={(l) => `Date: ${l}`} />
                    <Line type="monotone" dataKey="pass_rate" stroke="#6366f1" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-56 flex items-center justify-center text-slate-400 text-sm">No trend data available</div>
              )}
            </div>

            {/* 2. Pie Chart — Defect Categories */}
            <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
              <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2 text-sm">
                <AlertTriangle size={16} className="text-amber-500" /> Defect Categories
              </h3>
              {loading ? (
                <SkeletonBox className="h-56 w-full" />
              ) : defectData.by_category?.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={defectData.by_category}
                      dataKey="count"
                      nameKey="category"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      label={({ category, percent }) => `${category} ${(percent * 100).toFixed(0)}%`}
                    >
                      {defectData.by_category.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-56 flex items-center justify-center text-slate-400 text-sm">No defect data</div>
              )}
            </div>

            {/* 3. Bar Chart — Rework Aging */}
            <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
              <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2 text-sm">
                <Clock size={16} className="text-orange-500" /> Rework Aging — {agingData.total_open || 0} Open
              </h3>
              {loading ? (
                <SkeletonBox className="h-56 w-full" />
              ) : agingData.buckets?.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={agingData.buckets}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                      {agingData.buckets.map((entry, i) => (
                        <Cell key={i} fill={AGING_COLORS[entry.label] || '#94a3b8'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-56 flex items-center justify-center text-emerald-500 text-sm">
                  <CheckCircle2 size={20} className="mr-2" /> No open reworks
                </div>
              )}
              {!loading && escalatedReworks.length > 0 && (
                <div className="mt-4 border border-red-200 rounded-lg overflow-hidden">
                  <div className="px-3 py-2 bg-red-50 text-red-700 text-xs font-bold uppercase">
                    Escalated Reworks (7d+)
                  </div>
                  <div className="max-h-36 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 text-slate-500">
                        <tr>
                          <th className="px-2 py-1.5 text-left">Job Card</th>
                          <th className="px-2 py-1.5 text-left">Stage</th>
                          <th className="px-2 py-1.5 text-center">Age</th>
                          <th className="px-2 py-1.5 text-left">Reason</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {escalatedReworks.map((item) => (
                          <tr key={`esc-${item.id}`}>
                            <td className="px-2 py-1.5 font-medium text-slate-800">{item.job_card}</td>
                            <td className="px-2 py-1.5 text-slate-700">{item.stage}</td>
                            <td className="px-2 py-1.5 text-center text-red-700 font-semibold">{item.age_days}d</td>
                            <td className="px-2 py-1.5 text-slate-600">{item.reason || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            {/* 4. Inspector Performance Table */}
            <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
              <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2 text-sm">
                <User size={16} className="text-indigo-600" /> Inspector Performance
              </h3>
              {loading ? (
                <div className="space-y-3">
                  {Array.from({ length: 4 }).map((_, i) => <SkeletonBox key={i} className="h-8 w-full" />)}
                </div>
              ) : inspectorData.length > 0 ? (
                <div className="overflow-x-auto max-h-52">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 text-slate-500 uppercase sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left">Inspector</th>
                        <th className="px-3 py-2 text-center">Reviews</th>
                        <th className="px-3 py-2 text-center">Pass Rate</th>
                        <th className="px-3 py-2 text-center">Avg Time</th>
                        <th className="px-3 py-2 text-center">Approved</th>
                        <th className="px-3 py-2 text-center">Rejected</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {inspectorData.map((ins, i) => (
                        <tr key={i} className="hover:bg-slate-50">
                          <td className="px-3 py-2 font-medium text-slate-800">{ins.inspector || 'Unknown'}</td>
                          <td className="px-3 py-2 text-center text-slate-700">{ins.total_reviewed}</td>
                          <td className="px-3 py-2 text-center">
                            <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${ins.pass_rate >= 90 ? 'bg-emerald-100 text-emerald-700' : ins.pass_rate >= 70 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                              {ins.pass_rate}%
                            </span>
                          </td>
                          <td className="px-3 py-2 text-center text-indigo-700 font-semibold">{ins.avg_minutes ?? '--'}m</td>
                          <td className="px-3 py-2 text-center text-emerald-600 font-semibold">{ins.approved}</td>
                          <td className="px-3 py-2 text-center text-red-600 font-semibold">{ins.rejected}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="h-48 flex items-center justify-center text-slate-400 text-sm">No inspector data</div>
              )}
            </div>
          </div>

          {/* QC QUEUE HEAT MAP */}
          <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-slate-800 flex items-center gap-2 text-sm">
                <Zap size={16} className="text-amber-500" /> QC Queue — {queueData.length} Pending
              </h3>
              <button onClick={() => onNavigate('QC_QUEUE')} className="text-xs text-indigo-600 hover:text-indigo-800 font-medium flex items-center gap-1">
                View Full Queue <ArrowUpRight size={14} />
              </button>
            </div>
            {loading ? (
              <div className="space-y-2">
                {Array.from({ length: 4 }).map((_, i) => <SkeletonBox key={i} className="h-12 w-full" />)}
              </div>
            ) : queueData.length > 0 ? (
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {queueData.slice(0, 20).map((item, i) => (
                  <div
                    key={item.stage_id || i}
                    onClick={() => onNavigate('QC_REVIEW', item)}
                    className="flex items-center justify-between p-3 rounded-lg border border-slate-100 hover:border-slate-300 cursor-pointer transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <WaitBadge minutes={item.waiting_minutes || 0} />
                      <div>
                        <span className="font-semibold text-sm text-slate-800">{item.job_card_no}</span>
                        <span className="text-xs text-slate-400 ml-2">{item.design_code}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-1 rounded">{item.stage_name?.replace(/_/g, ' ')}</span>
                      <ChevronRight size={14} className="text-slate-400" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-8 text-center text-slate-400 text-sm">
                <CheckCircle2 size={24} className="mx-auto mb-2 text-emerald-300" /> All clear — no pending QC items
              </div>
            )}
          </div>

          {/* INSPECTOR WORKLOAD */}  
          <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-slate-800 flex items-center gap-2 text-sm">
                <User size={16} className="text-indigo-600" /> Inspector Workload
              </h3>
              <span className="text-xs text-slate-500">{inspectorWorkload.reduce((sum, row) => sum + (row.pending_count || 0), 0)} pending</span>
            </div>
            {loading ? (
              <div className="space-y-2">
                {Array.from({ length: 5 }).map((_, i) => <SkeletonBox key={i} className="h-10 w-full" />)}
              </div>
            ) : (
              <>
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-slate-500 uppercase">
                    <tr>
                      <th className="px-3 py-2 text-left">Inspector</th>
                      <th className="px-3 py-2 text-center">Pending</th>
                      <th className="px-3 py-2 text-left">Stage</th>
                      <th className="px-3 py-2 text-left">Job Card</th>
                      <th className="px-3 py-2 text-center">Wait</th>
                      <th className="px-3 py-2 text-left">Reassign</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {inspectorWorkload.flatMap((row) => (
                      (row.stages || []).map((stage) => (
                        <tr key={`${row.inspector_id || 'unassigned'}-${stage.stage_id}`} className="hover:bg-slate-50">
                          <td className="px-3 py-2 font-medium text-slate-800">{row.name}</td>
                          <td className="px-3 py-2 text-center">
                            <span className="px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 font-bold">{row.pending_count}</span>
                          </td>
                          <td className="px-3 py-2 text-slate-700">{stage.stage_name}</td>
                          <td className="px-3 py-2 text-slate-700">{stage.job_card_no}</td>
                          <td className="px-3 py-2 text-center text-slate-600">{stage.waiting_minutes}m</td>
                          <td className="px-3 py-2">
                            <select
                              className="border border-slate-200 rounded-md px-2 py-1 text-xs w-full max-w-[180px]"
                              value={stage.assigned_inspector_id || ''}
                              onChange={(e) => handleReassignStage(stage.stage_id, e.target.value)}
                              disabled={reassigningStageId === stage.stage_id}
                            >
                              <option value="">Select Inspector</option>
                              {availableInspectors.map((ins) => (
                                <option key={ins.inspector_id} value={ins.inspector_id}>
                                  {ins.name}
                                </option>
                              ))}
                            </select>
                          </td>
                        </tr>
                      ))
                    ))}
                  </tbody>
                </table>
                {inspectorWorkload.every((row) => (row.pending_count || 0) === 0) && (
                  <div className="text-center py-6 text-slate-400">No pending workload data</div>
                )}
              </div>
              <div className="md:hidden space-y-3">
                {inspectorWorkload.flatMap((row) => (row.stages || []).map((stage) => (
                  <div key={`card-${row.inspector_id || 'na'}-${stage.stage_id}`} className="border border-slate-200 rounded-lg p-3 bg-slate-50">
                    <div className="flex items-center justify-between">
                      <div className="font-semibold text-slate-800">{row.name}</div>
                      <span className="px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 text-[11px] font-bold">{row.pending_count}</span>
                    </div>
                    <div className="mt-2 text-xs text-slate-600">
                      <div>{stage.job_card_no}</div>
                      <div>{stage.stage_name} - {stage.waiting_minutes}m</div>
                    </div>
                    <select
                      className="mt-2 w-full border border-slate-300 rounded-md px-2 py-2 text-sm"
                      value={stage.assigned_inspector_id || ''}
                      onChange={(e) => handleReassignStage(stage.stage_id, e.target.value)}
                      disabled={reassigningStageId === stage.stage_id}
                    >
                      <option value="">Select Inspector</option>
                      {availableInspectors.map((ins) => (
                        <option key={ins.inspector_id} value={ins.inspector_id}>{ins.name}</option>
                      ))}
                    </select>
                  </div>
                )))}
                {inspectorWorkload.every((row) => (row.pending_count || 0) === 0) && (
                  <div className="text-center py-6 text-slate-400">No pending workload data</div>
                )}
              </div>
              </>
            )}
          </div>

          {/* REWORK COST ROW — kept from original */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="bg-white p-5 rounded-xl border border-red-200 shadow-sm">
              <p className="text-xs font-bold uppercase tracking-wide text-red-600">Rework Cost Summary</p>
              <h3 className="text-2xl font-bold text-slate-800 mt-2">
                {formatCurrency(reworkCostReport.summary?.total_rework_cost || 0)}
              </h3>
              <p className="text-sm text-slate-600 mt-1">
                {reworkCostReport.summary?.rework_count || 0} reworks | Avg {formatCurrency(reworkCostReport.summary?.avg_per_rework || 0)}
              </p>
            </div>
            <div className="lg:col-span-2 bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
              <h3 className="font-bold text-slate-800 mb-3 flex items-center gap-2 text-sm">
                <BarChart3 size={16} className="text-rose-600" /> Costliest Rework Stages
              </h3>
              <div className="space-y-3">
                {(reworkCostReport.by_stage || []).slice(0, 5).map((item) => {
                  const maxCost = Math.max(...(reworkCostReport.by_stage || []).slice(0, 5).map(s => s.total_cost || 0), 1);
                  return (
                    <CostStageBar key={item.stage_code || item.stage_name} stage={item.stage_name || item.stage_code} totalCost={item.total_cost} maxCost={maxCost} />
                  );
                })}
                {(!reworkCostReport.by_stage || reworkCostReport.by_stage.length === 0) && (
                  <div className="text-center py-4 text-slate-400 text-sm">No rework cost data</div>
                )}
              </div>
            </div>
          </div>

          {canManageBypass && (
            <QCDashboardBypassSection
              eligibleWorkers={eligibleWorkers}
              activeRules={activeBypassRules}
              loading={false}
              actionKey={bypassActionKey}
              bypassError={bypassError}
              onEnable={handleEnableBypass}
              onDisable={handleDisableBypass}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// --- QC QUEUE SCREEN ---

const QCQueueScreen = ({ onReview }) => {
  const [queueData, setQueueData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('ALL');

  useEffect(() => {
    fetchQueue();
  }, []);

  const fetchQueue = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await QCService.getQueue();
      setQueueData(data);
    } catch (err) {
      console.error('Failed to fetch queue:', err);
      setError('Failed to load queue data');
    } finally {
      setLoading(false);
    }
  };

  const filteredData = queueData.filter(item => {
    const matchesSearch =
      (item.job_card_no || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.design_code || '').toLowerCase().includes(searchTerm.toLowerCase());

    const matchesFilter = filterType === 'ALL' || item.stage_type === filterType;
    return matchesSearch && matchesFilter;
  });

  const avgWaitTime = filteredData.length > 0
    ? Math.floor(filteredData.reduce((acc, item) => acc + item.waiting_minutes, 0) / filteredData.length)
    : 0;

  return (
    <div className="flex flex-col h-full bg-slate-50/50 overflow-hidden text-black">
      <div className="px-8 py-5 border-b border-slate-200 bg-white">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">QC Queue</h1>
            <p className="text-slate-500 mt-1">Manage pending approvals and stage prioritization</p>
            {error && <p className="text-sm text-red-500 mt-1">{error}</p>}
          </div>
          <div className="flex items-center gap-3">
            <div className="bg-slate-100 text-slate-600 px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-2">
              <Clock size={16} />
              Avg Wait: {loading ? '...' : `${avgWaitTime}m`}
            </div>
            <button
              onClick={fetchQueue}
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg text-sm font-bold shadow-sm flex items-center gap-2"
            >
              <RefreshCcw size={16} /> Refresh List
            </button>
          </div>
        </div>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-2.5 text-slate-400" size={16} />
              <input
                type="text"
                placeholder="Search Job / Design..."
                className="pl-9 pr-4 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 w-64"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="h-8 w-px bg-slate-300 mx-2"></div>
            <div className="flex bg-slate-100 p-1 rounded-lg">
              {['ALL', 'PRE', 'CASTING', 'POST'].map(type => (

                <button
                  key={type}
                  onClick={() => setFilterType(type)}
                  className={`px-3 py-1.5 text-xs font-bold rounded-md transition-all ${filterType === type ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  {type === 'ALL'
                    ? 'All Stages'
                    : type === 'PRE'
                      ? 'Pre-Casting'
                      : type === 'CASTING'
                        ? 'Casting'
                        : 'Post-Casting'}

                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button className="p-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 hover:text-emerald-600">
              <SlidersHorizontal size={18} />
            </button>
            <button className="px-3 py-2 border border-slate-200 rounded-lg text-slate-600 text-sm font-medium hover:bg-slate-50 flex items-center gap-2">
              Sort: Priority <ChevronDown size={14} />
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-8">
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-8 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
            </div>
          ) : filteredData.length > 0 ? (
            <table className="w-full text-sm text-left">
              <thead className="bg-slate-50 text-slate-500 uppercase font-semibold text-xs border-b border-slate-200">
                <tr>
                  <th className="px-6 py-4">Job Card / Design</th>
                  <th className="px-6 py-4">Stage</th>
                  <th className="px-6 py-4">Submission</th>
                  <th className="px-6 py-4">Waiting Time</th>
                  <th className="px-6 py-4 text-center">Priority</th>
                  <th className="px-6 py-4 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredData.map((item) => (
                  <tr key={item.stage_id} className="hover:bg-slate-50 transition-colors group">
                    <td className="px-6 py-4">
                      <div className="font-bold text-slate-800 text-base">{item.job_card_no}</div>
                      <div className="text-xs text-slate-500 mt-1 font-medium">{item.design_code}</div>
                      <div className="text-[10px] text-slate-400">{item.design_name}</div>
                      {(item.stone_count || 0) > 0 && (
                        <div className="mt-2 inline-flex items-center gap-1 rounded-full border border-fuchsia-200 bg-fuchsia-50 px-2 py-1 text-[10px] font-bold text-fuchsia-700">
                          <Gem size={10} /> {item.stone_count} stone type{item.stone_count !== 1 ? 's' : ''} issued
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`px-2.5 py-1 rounded text-[10px] font-bold uppercase tracking-wide border
                              ${item.stage_type === 'PRE'
                            ? 'bg-orange-50 text-orange-700 border-orange-100'
                            : item.stage_type === 'CASTING'
                              ? 'bg-blue-50 text-blue-700 border-blue-100'
                              : 'bg-purple-50 text-purple-700 border-purple-100'
                          }`}
                      >

                        {item.stage_name.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-slate-700 font-medium">{item.submitted_by}</div>
                      <div className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                        <Clock size={10} /> {new Date(item.submitted_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <WaitBadge minutes={item.waiting_minutes} />
                    </td>
                    <td className="px-6 py-4 text-center">
                      {item.priority === 'HIGH' ? (
                        <div className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-red-100 text-red-700 text-xs font-bold">
                          <Zap size={12} fill="currentColor" /> High
                        </div>
                      ) : (
                        <div className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-slate-100 text-slate-600 text-xs font-bold">Normal</div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => onReview(item)}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg text-xs font-bold shadow-sm transition-all transform active:scale-95 flex items-center gap-2 ml-auto"
                      >
                        Review <ArrowRight size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="p-8 text-center text-slate-400">
              No items in QC queue
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// --- QC REVIEW SCREEN ---

const QCReviewScreen = ({ qcItem, onBack, onDecision }) => {
  const { getOptions } = useMasterData(['DEFECT_TYPE', 'DEFECT_REASON', 'QC_RESOLUTION', 'CONFLICT_REASON']);
  const defectTypeOptions = getOptions('DEFECT_TYPE');
  const defectReasonOptions = getOptions('DEFECT_REASON');
  const qcResolutionOptions = getOptions('QC_RESOLUTION');
  const conflictReasonOptions = getOptions('CONFLICT_REASON');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [photosLoading, setPhotosLoading] = useState(false);
  const [photoUploadLoading, setPhotoUploadLoading] = useState(false);
  const [evidenceFiles, setEvidenceFiles] = useState([]);
  const [evidencePreviews, setEvidencePreviews] = useState([]);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const [remarks, setRemarks] = useState('');
  const [activeAction, setActiveAction] = useState(null);
  const [formData, setFormData] = useState({
    total_checked_qty: 0,
    approved_qty: 0,
    rejected_qty: 0,
    after_weight: '',
    defect_type: '',
    defect_reason: '',
    defect_action: 'REWORK'
  });
  const [varianceAcknowledged, setVarianceAcknowledged] = useState(false);
  const [showReworkConfirm, setShowReworkConfirm] = useState(false);
  const [checklistState, setChecklistState] = useState({ checked: {}, criticalItems: [], extra: {} });
  const [weightVariance, setWeightVariance] = useState(null);
  const [stoneSummary, setStoneSummary] = useState([]);
  const [stoneReturnDrafts, setStoneReturnDrafts] = useState({});
  const [stoneReturnLoading, setStoneReturnLoading] = useState(false);

  useEffect(() => {
    if (!qcItem) return;

    const processedQty =
      qcItem.production_data?.processed_qty ??
      qcItem.processed_qty ??
      0;

    setFormData(prev => ({
      ...prev,
      total_checked_qty: processedQty,
      approved_qty: processedQty,
      rejected_qty: 0,
      after_weight: ''
    }));

    // Fetch weight variance
    if (qcItem.stage_id) {
      QCService.getWeightVariance(qcItem.stage_id).then(data => {
        if (data) {
          setWeightVariance(data);
          setFormData((prev) => ({
            ...prev,
            after_weight: data.actual_weight !== undefined && data.actual_weight !== null ? String(data.actual_weight) : prev.after_weight,
          }));
        }
      });

      setPhotosLoading(true);
      QCService.getStagePhotos(qcItem.stage_id)
        .then((data) => setPhotos(data))
        .finally(() => setPhotosLoading(false));
    }

    setEvidenceFiles([]);
    setEvidencePreviews([]);
    setLightboxPhoto(null);
    setVarianceAcknowledged(false);
    setStoneSummary(
      Array.isArray(qcItem.stone_summary)
        ? qcItem.stone_summary.map((stone) => ({
          material_id: stone.material_id || null,
          stone_name: stone.stone_name,
          stone_type: stone.stone_type,
          size: stone.size,
          quality_grade: stone.quality_grade,
          clarity: stone.clarity,
          cut: stone.cut,
          color: stone.color,
          unit: stone.unit || 'PCS',
          qty_issued: Number(stone.qty_issued ?? stone.count ?? 0),
          qty_used: Number(stone.qty_used ?? stone.used_qty ?? 0),
          qty_returned: Number(stone.qty_returned ?? stone.returned_qty ?? 0),
          qty_wastage: Number(stone.qty_wastage ?? stone.wasted_qty ?? 0),
          qty_pending: Number(stone.qty_pending ?? 0),
        }))
        : []
    );
    setStoneReturnDrafts({});
  }, [qcItem]);

  const beforePhotos = photos.filter((photo) => photo.photo_type?.toLowerCase() === 'before');
  const afterPhotos = photos.filter((photo) => photo.photo_type?.toLowerCase() === 'after');

  const handleChecklistChange = (checked, criticalItems, extra = {}) => {
    setChecklistState({ checked, criticalItems, extra });
  };

  const failedRequiredCount = (checklistState.extra?.failedRequired || []).length;
  const evidenceRequired = activeAction === 'REJECT' || (activeAction === 'APPROVE' && formData.rejected_qty > 0);
  const expectedWeight = Number(weightVariance?.expected_weight || 0);
  const actualWeight = Number(formData.after_weight || 0);
  const currentVariancePct = expectedWeight > 0 ? Math.abs(expectedWeight - actualWeight) / expectedWeight * 100 : 0;
  const isVarianceWarn = currentVariancePct > 2 && currentVariancePct <= 5;
  const isVarianceHigh = currentVariancePct > 5;

  const handleEvidenceChange = (event) => {
    const selected = Array.from(event.target.files || []);
    setEvidenceFiles(selected);
    setEvidencePreviews(selected.map((f) => URL.createObjectURL(f)));
  };

  const handleStoneReturnDraftChange = (stone, index, value) => {
    const draftKey = String(stone.material_id || `${stone.stone_name || 'stone'}-${stone.size || index}`);
    setStoneReturnDrafts((prev) => ({ ...prev, [draftKey]: value }));
  };

  const handleReturnStones = async () => {
    const payload = stoneSummary
      .map((stone, index) => {
        const draftKey = String(stone.material_id || `${stone.stone_name || 'stone'}-${stone.size || index}`);
        const returnedQty = Number(stoneReturnDrafts[draftKey] || 0);
        if (returnedQty <= 0) return null;
        return {
          material_id: stone.material_id || null,
          stone_name: stone.stone_name,
          size: stone.size,
          returned_qty: returnedQty,
        };
      })
      .filter(Boolean);

    if (payload.length === 0) {
      alert('Enter at least one unused stone quantity to return.');
      return;
    }

    setStoneReturnLoading(true);
    try {
      const result = await QCService.returnStageStones(qcItem.stage_id, payload);
      setStoneSummary(Array.isArray(result?.stone_summary) ? result.stone_summary.map((stone) => ({
        material_id: stone.material_id || null,
        stone_name: stone.stone_name,
        stone_type: stone.stone_type,
        size: stone.size,
        quality_grade: stone.quality_grade,
        clarity: stone.clarity,
        cut: stone.cut,
        color: stone.color,
        unit: stone.unit || 'PCS',
        qty_issued: Number(stone.count ?? stone.qty_issued ?? 0),
        qty_used: Number(stone.used_qty ?? stone.qty_used ?? 0),
        qty_returned: Number(stone.returned_qty ?? stone.qty_returned ?? 0),
        qty_wastage: Number(stone.wasted_qty ?? stone.qty_wastage ?? 0),
        qty_pending: Math.max(
          Number(stone.count ?? stone.qty_issued ?? 0)
          - Number(stone.used_qty ?? stone.qty_used ?? 0)
          - Number(stone.returned_qty ?? stone.qty_returned ?? 0)
          - Number(stone.wasted_qty ?? stone.qty_wastage ?? 0),
          0
        ),
      })) : stoneSummary);
      setStoneReturnDrafts({});
      alert(result?.message || 'Unused stones returned successfully.');
    } catch (err) {
      console.error('Stone return error:', err);
      alert(err?.response?.data?.message || 'Failed to return stones.');
    } finally {
      setStoneReturnLoading(false);
    }
  };

  if (!qcItem) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-slate-400">Loading QC review...</div>
      </div>
    );
  }

  const handleSubmit = async () => {
    if (!activeAction) {
      alert("Please select an action");
      return;
    }

    if (activeAction === 'APPROVE' && failedRequiredCount > 0) {
      alert(`Cannot fully approve: ${failedRequiredCount} required checklist item(s) failed.`);
      return;
    }

    if (activeAction !== 'APPROVE' && !remarks) {
      alert("Remarks are mandatory for Rejection or Conflict.");
      return;
    }
    if (evidenceRequired && evidenceFiles.length === 0) {
      alert("At least 1 photo required for rejection or approval with rework.");
      return;
    }
    if (activeAction === 'APPROVE' && isVarianceHigh && !varianceAcknowledged) {
      alert("High weight variance detected. Please acknowledge before approving.");
      return;
    }

    if (formData.total_checked_qty <= 0) {
      alert("Total Pieces must be greater than 0.");
      return;
    }

    if (formData.approved_qty > formData.total_checked_qty) {
      alert("Accepted Pieces cannot exceed Total Pieces.");
      return;
    }

    // Confirm rework if defective pieces exist
    if (formData.rejected_qty > 0 && !showReworkConfirm) {
      setShowReworkConfirm(true);
      return;
    }
    setShowReworkConfirm(false);

    setLoading(true);
    setError(null);

    try {
      const reviewData = {
        decision: activeAction,
        total_pieces: formData.total_checked_qty,
        accepted_pieces: formData.approved_qty,
        total_checked_qty: formData.total_checked_qty,
        approved_qty: formData.approved_qty,
        rejected_qty: formData.rejected_qty,
        defect_type: activeAction === 'REJECT' ? formData.defect_type : null,
        defect_reason: activeAction === 'REJECT' ? formData.defect_reason : null,
        defect_action: activeAction === 'REJECT' ? formData.defect_action : null,
        defect_category: formData.defect_type || null,
        remarks,
        after_weight: formData.after_weight === '' ? null : Number(formData.after_weight),
        variance_acknowledged: varianceAcknowledged,
        has_evidence_photo: evidenceRequired ? evidenceFiles.length > 0 : false,
      };

      const result = await QCService.reviewStage(qcItem.stage_id, reviewData);
      if (result?.inspection_id && evidenceFiles.length > 0) {
        setPhotoUploadLoading(true);
        await QCService.uploadInspectionPhotos(result.inspection_id, evidenceFiles);
      }

      // Submit checklist responses (structured format)
      const clExtra = checklistState.extra || {};
      const clResponses = clExtra.responses || {};
      const checklistResponses = Object.entries(clResponses)
        .filter(([, r]) => r.passed !== null && r.passed !== undefined)
        .map(([itemId, r]) => ({
          item_id: parseInt(itemId) || itemId,
          passed: r.passed,
          notes: r.notes || '',
        }));
      if (checklistResponses.length > 0) {
        await QCService.submitChecklistResponse(qcItem.stage_id, {
          inspection_id: result.inspection_id || null,
          checklist_id: clExtra.checklistId || null,
          responses: checklistResponses,
        }).catch(() => {}); // non-blocking
      }

      const defectiveQty = Number(result?.defective_qty || formData.rejected_qty || 0);
      const successMessage = activeAction === 'APPROVE' && defectiveQty > 0
        ? `Stage approved. ${defectiveQty} piece(s) sent to rework queue. Next stage unlocked.`
        : `Stage ${activeAction.toLowerCase()} successful! Status: ${result.status}`;
      alert(successMessage);
      onDecision(activeAction, { ...reviewData, result });

    } catch (err) {
      console.error('Review submission error:', err);
      setError(err.response?.data?.error || 'Failed to submit review');
    } finally {
      setPhotoUploadLoading(false);
      setLoading(false);
    }
  };

  return (
    <div className="qc-modal flex flex-col h-full bg-slate-50/50 overflow-hidden md:relative fixed inset-0 z-40 md:z-auto bg-white md:bg-transparent">
      <div className="qc-modal-header px-4 md:px-8 py-4 md:py-5 border-b border-slate-200 bg-white shadow-sm">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3 md:gap-4">
            <button onClick={onBack} className="p-2 hover:bg-slate-100 rounded-full text-slate-500 transition-colors">
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-xl font-bold text-slate-800 flex items-center gap-3">
                QC Review: {qcItem?.stage_name || 'Stage'}
                {qcItem?.rework_count > 0 && (
                  <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded border border-orange-200 flex items-center gap-1">
                    <RefreshCcw size={10} /> Rework x{qcItem.rework_count}
                  </span>
                )}
              </h1>
              <div className="text-xs text-slate-500 mt-1 flex items-center gap-3">
                <span className="font-mono bg-slate-100 px-1.5 rounded">{qcItem?.job_card_no}</span>
                <span>•</span>
                <span>{qcItem?.design_code}</span>
                <span>•</span>
                <span className="text-indigo-600 font-medium">Submitted by {qcItem?.submitted_by}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs font-medium text-slate-500 bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-200">
            <Clock size={14} /> Stage ID: {qcItem?.stage_id}
          </div>
        </div>
        {weightVariance && (
          <div className="mt-3">
            <WeightVariancePanel variance={weightVariance} />
          </div>
        )}
        {(qcItem?.total_rework_cost || 0) > 0 && (
          <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-red-200 bg-red-50 px-4 py-2 text-sm font-semibold text-red-700">
            <AlertCircle size={15} />
            Quality Cost: Total rework cost so far {formatCurrency(qcItem.total_rework_cost)}
          </div>
        )}
      </div>

      {error && (
        <div className="mx-4 md:mx-8 mt-4 p-3 bg-red-50 border border-red-100 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      <div className="qc-modal-body flex-1 overflow-y-auto p-4 md:p-8">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="bg-slate-50 px-6 py-3 border-b border-slate-200">
                <h3 className="font-bold text-slate-700 text-sm flex items-center gap-2">
                  <CheckSquare size={16} className="text-indigo-600" /> Quality Inspection Data
                </h3>
                {/* PRODUCTION SUMMARY */}
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                  <div className="bg-slate-50 px-6 py-3 border-b border-slate-200">
                    <h3 className="font-bold text-slate-700 text-sm">
                      Production Summary
                    </h3>
                  </div>

                  <div className="p-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-sm">
                    <div>
                      <div className="text-xs text-slate-400 font-bold uppercase">
                        Planned Qty
                      </div>
                      <div className="text-lg font-mono text-black">
                        {qcItem.production_data?.planned_qty ?? '-'}
                      </div>
                    </div>

                    <div>
                      <div className="text-xs text-slate-400 font-bold uppercase">
                        Processed Qty
                      </div>
                      <div className="text-lg font-mono text-emerald-600">
                        {qcItem.production_data?.processed_qty ?? '-'}
                      </div>
                    </div>

                    <div>
                      <div className="text-xs text-slate-400 font-bold uppercase">
                        Process Method
                      </div>
                      <div className="text-sm font-medium text-black">
                        {qcItem.stage_type
                          ?? '-'}
                      </div>
                    </div>

                    <div>
                      <div className="text-xs text-slate-400 font-bold uppercase">
                        Notes
                      </div>
                      <div className="text-sm text-slate-700">
                        {qcItem.production_data?.notes || '—'}
                      </div>
                    </div>
                  </div>
                </div>

                {qcItem?.stage_code && (() => {
                  const stageTheme = getStagePanelTheme(qcItem.stage_code);
                  const completionSummary = qcItem.production_data?.completion_summary || {};

                  return (
                    <div className="mt-4 space-y-3">
                      <div className={`rounded-xl border px-6 py-3 ${stageTheme.bg} ${stageTheme.border}`}>
                        <h3 className={`font-bold text-sm ${stageTheme.text}`}>
                          Stage-Specific Production Data — {qcItem.stage_code}
                        </h3>
                      </div>
                      <StageQCDataPanel
                        stageCode={qcItem.stage_code}
                        completionSummary={completionSummary}
                      />
                    </div>
                  );
                })()}

                {/* WEIGHT ANALYSIS */}
                {(() => {
                  const pd = qcItem.production_data || {};
                  const cs = pd.completion_summary || pd;
                  const bw = parseFloat(cs.before_weight || 0);
                  const aw = parseFloat(cs.after_weight || 0);
                  const metalLoss = parseFloat(cs.metal_loss_pct || (bw > 0 ? ((bw - aw) / bw * 100) : 0));
                  const wastageG = bw - aw;
                  const netPieces = cs.net_pieces ?? cs.pieces_produced ?? '-';

                  if (bw <= 0 && aw <= 0) return null;

                  return (
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden mt-4">
                      <div className="bg-indigo-50 px-6 py-3 border-b border-indigo-100">
                        <h3 className="font-bold text-indigo-700 text-sm flex items-center gap-2">
                          <Scale size={16} /> Weight Analysis
                        </h3>
                      </div>
                      <div className="p-6 grid grid-cols-4 gap-4">
                        <div className="text-center p-3 bg-slate-50 rounded-lg border">
                          <div className="text-[10px] text-slate-400 font-bold uppercase">Input Weight</div>
                          <div className="text-lg font-bold font-mono text-slate-800">{bw.toFixed(3)}g</div>
                        </div>
                        <div className="text-center p-3 bg-slate-50 rounded-lg border">
                          <div className="text-[10px] text-slate-400 font-bold uppercase">Output Weight</div>
                          <div className="text-lg font-bold font-mono text-slate-800">{aw.toFixed(3)}g</div>
                        </div>
                        <div className="text-center p-3 bg-slate-50 rounded-lg border">
                          <div className="text-[10px] text-slate-400 font-bold uppercase">Metal Loss</div>
                          <div className={`text-lg font-bold font-mono ${metalLoss > 3 ? 'text-red-600' : metalLoss > 1 ? 'text-amber-600' : 'text-emerald-600'}`}>
                            {metalLoss.toFixed(2)}%
                          </div>
                          <div className={`text-[10px] font-bold ${metalLoss > 3 ? 'text-red-500' : metalLoss > 1 ? 'text-amber-500' : 'text-emerald-500'}`}>
                            {metalLoss > 3 ? '⚠ ABOVE SPEC' : metalLoss > 1 ? 'BORDERLINE' : '✓ WITHIN SPEC'}
                          </div>
                        </div>
                        <div className="text-center p-3 bg-slate-50 rounded-lg border">
                          <div className="text-[10px] text-slate-400 font-bold uppercase">Net Pieces</div>
                          <div className="text-lg font-bold font-mono text-slate-800">{netPieces}</div>
                          {wastageG > 0 && (
                            <div className="text-[10px] text-orange-600 font-bold">Wastage: {wastageG.toFixed(3)}g</div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })()}

              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-end">
                  <div>
                    <label className="text-xs text-slate-400 font-bold uppercase block mb-1">Total Pieces</label>
                    <input
                      type="number"
                      min="0"
                      value={formData.total_checked_qty}
                      onChange={(e) => {
                        const total = parseInt(e.target.value) || 0;
                        const accepted = Math.min(formData.approved_qty, total);
                        const defective = Math.max(total - accepted, 0);
                        setFormData({ ...formData, total_checked_qty: total, approved_qty: accepted, rejected_qty: defective });
                      }}
                      className="w-full p-2 border border-slate-300 rounded-md text-lg font-mono text-slate-800 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 font-bold uppercase block mb-1">Accepted Pieces</label>
                    <input
                      type="number"
                      min="0"
                      max={formData.total_checked_qty}
                      value={formData.approved_qty}
                      onChange={(e) => {
                        const accepted = Math.min(parseInt(e.target.value) || 0, formData.total_checked_qty);
                        const defective = Math.max(formData.total_checked_qty - accepted, 0);
                        setFormData({ ...formData, approved_qty: accepted, rejected_qty: defective });
                      }}
                      className="w-full p-2 border border-emerald-300 rounded-md text-lg font-mono text-emerald-700 font-bold focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 font-bold uppercase block mb-1">Defective Pieces</label>
                    <input
                      type="number"
                      value={formData.rejected_qty}
                      readOnly
                      tabIndex={-1}
                      className="w-full p-2 border border-yellow-400 rounded-md text-lg font-mono font-bold cursor-not-allowed"
                      style={{ backgroundColor: formData.rejected_qty > 0 ? '#FEF3C7' : '#F1F5F9', color: formData.rejected_qty > 0 ? '#B45309' : '#64748B' }}
                    />
                  </div>
                  {/* Inline donut chart */}
                  <div className="flex items-center justify-center">
                    {formData.total_checked_qty > 0 ? (() => {
                      const total = formData.total_checked_qty;
                      const accepted = formData.approved_qty;
                      const acceptPct = total > 0 ? (accepted / total) * 100 : 100;
                      const circumference = 2 * Math.PI * 36;
                      const acceptLen = (acceptPct / 100) * circumference;
                      const defectLen = circumference - acceptLen;
                      return (
                        <div className="relative" style={{ width: 80, height: 80 }}>
                          <svg viewBox="0 0 80 80" width="80" height="80">
                            {/* Defective (red) background circle */}
                            <circle cx="40" cy="40" r="36" fill="none" stroke="#EF4444" strokeWidth="8" />
                            {/* Accepted (green) arc */}
                            <circle cx="40" cy="40" r="36" fill="none" stroke="#10B981" strokeWidth="8"
                              strokeDasharray={`${acceptLen} ${defectLen}`}
                              strokeDashoffset={circumference * 0.25}
                              strokeLinecap="round"
                              style={{ transition: 'stroke-dasharray 0.3s ease' }}
                            />
                          </svg>
                          <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <span className="text-xs font-bold text-slate-700">{Math.round(acceptPct)}%</span>
                            <span className="text-[8px] text-slate-400 uppercase">Pass</span>
                          </div>
                        </div>
                      );
                    })() : (
                      <div className="w-20 h-20 rounded-full border-4 border-dashed border-slate-200 flex items-center justify-center">
                        <span className="text-[9px] text-slate-400">No data</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Rework warning */}
                {formData.rejected_qty > 0 && (
                  <div className="mt-4 flex items-start gap-3 p-3 rounded-lg border border-red-200 bg-red-50">
                    <AlertTriangle size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="text-sm font-bold text-red-700">
                        {formData.rejected_qty} piece{formData.rejected_qty !== 1 ? 's' : ''} will be sent to Rework queue
                      </div>
                      <div className="text-xs text-red-500 mt-0.5">
                        Accepted: {formData.approved_qty} / {formData.total_checked_qty} &bull; Defect rate: {formData.total_checked_qty > 0 ? ((formData.rejected_qty / formData.total_checked_qty) * 100).toFixed(1) : 0}%
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {qcItem?.stage_code && (
              <ChecklistPanel
                stageCode={qcItem.stage_code}
                onChecklistChange={handleChecklistChange}
              />
            )}

            <MaterialConsumptionTable materials={qcItem.material_summary} />
            <StoneConsumptionTable
              stones={stoneSummary}
              returnDrafts={stoneReturnDrafts}
              onReturnDraftChange={handleStoneReturnDraftChange}
              onReturnStones={handleReturnStones}
              loading={stoneReturnLoading}
            />

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="bg-slate-50 px-6 py-3 border-b border-slate-200 flex items-center justify-between">
                <h3 className="font-bold text-slate-700 text-sm flex items-center gap-2">
                  <Camera size={16} className="text-indigo-600" /> Stage Photos
                </h3>
                <span className="text-[10px] font-bold uppercase text-slate-400">
                  {photos.length} attached
                </span>
              </div>
              <div className="p-6 grid grid-cols-2 gap-4">
                {[
                  { key: 'before', label: 'Before Photos', items: beforePhotos },
                  { key: 'after', label: 'After Photos', items: afterPhotos }
                ].map((group) => (
                  <div key={group.key} className="rounded-lg border border-slate-200 p-4 bg-slate-50/60">
                    <div className="text-xs font-bold uppercase text-slate-500 mb-3">{group.label}</div>
                    {photosLoading ? (
                      <div className="text-sm text-slate-400">Loading photos...</div>
                    ) : group.items.length > 0 ? (
                      <div className="grid grid-cols-2 gap-3">
                        {group.items.map((photo) => (
                          <button
                            key={photo.id}
                            type="button"
                            onClick={() => setLightboxPhoto(photo)}
                            className="text-left rounded-lg overflow-hidden border border-slate-200 bg-white hover:border-indigo-300 transition-colors"
                          >
                            <img
                              src={toAbsolutePhotoUrl(photo.photo_url)}
                              alt={`${group.label} ${photo.id}`}
                              className="w-full h-28 object-cover"
                            />
                            <div className="p-2">
                              <div className="text-[10px] font-semibold text-slate-500 uppercase">
                                {photo.remarks || group.label}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="text-sm text-slate-400">No {group.key} photos attached.</div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {evidenceRequired && (
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden animate-in fade-in">
                <div className="bg-red-50 px-6 py-3 border-b border-red-100">
                  <h3 className="font-bold text-red-800 text-sm flex items-center gap-2">
                    <AlertTriangle size={16} /> Rejection Evidence
                  </h3>
                </div>
                <div className="px-6 pb-6">
                  {activeAction === 'REJECT' && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div>
                        <label className="text-xs text-slate-400 font-bold uppercase block mb-1">Defect Type</label>
                        <select
                          value={formData.defect_type}
                          onChange={(e) => setFormData({ ...formData, defect_type: e.target.value })}
                          className="w-full p-2 border border-slate-300 rounded-md text-sm"
                        >
                          <option value="">Select defect type</option>
                          {defectTypeOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label || option.value}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs text-slate-400 font-bold uppercase block mb-1">Defect Reason</label>
                        <select
                          value={formData.defect_reason}
                          onChange={(e) => setFormData({ ...formData, defect_reason: e.target.value })}
                          className="w-full p-2 border border-slate-300 rounded-md text-sm"
                        >
                          <option value="">Select reason...</option>
                          {defectReasonOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label || option.value}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs text-slate-400 font-bold uppercase block mb-1">Action Required</label>
                        <select
                          value={formData.defect_action}
                          onChange={(e) => setFormData({ ...formData, defect_action: e.target.value })}
                          className="w-full p-2 border border-slate-300 rounded-md text-sm"
                        >
                          {qcResolutionOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label || option.value}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}
                  <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 flex items-center gap-2">
                    <AlertTriangle size={14} /> At least 1 photo required for rejection/partial approve
                  </div>
                  <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4">
                    <div className="flex items-center justify-between gap-4 mb-3">
                      <div>
                        <div className="text-xs font-bold uppercase text-slate-500">QC Evidence Photos</div>
                        <div className="text-sm text-slate-500">Attach one or more photos. They will upload after inspection is created.</div>
                      </div>
                      <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-slate-300 text-sm font-semibold text-slate-700 cursor-pointer hover:bg-slate-100">
                        <UploadCloud size={16} />
                        Choose Photos
                        <input type="file" multiple accept="image/*" className="hidden" onChange={handleEvidenceChange} />
                      </label>
                    </div>
                    {evidencePreviews.length > 0 && (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {evidencePreviews.map((src, idx) => (
                          <img
                            key={`evidence-${idx}`}
                            src={src}
                            alt={`Evidence ${idx + 1}`}
                            className="w-full h-24 rounded-lg object-cover border border-slate-200"
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-6">
            <div className="qc-modal-footer bg-white rounded-xl border border-slate-200 shadow-lg overflow-hidden">
              <div className="bg-slate-900 px-5 py-3 border-b border-slate-800">
                <h3 className="font-bold text-white text-sm flex items-center gap-2">
                  <Lock size={16} /> QC Decision
                </h3>
              </div>
              <div className="p-5 space-y-4">
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <div className="text-xs font-bold uppercase text-slate-500 mb-2">Weight Verification</div>
                  <div className="grid grid-cols-1 gap-2 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500">Expected Weight</span>
                      <span className="font-mono font-bold text-slate-800">{expectedWeight ? expectedWeight.toFixed(3) : '0.000'} g</span>
                    </div>
                    <label className="flex items-center justify-between gap-2">
                      <span className="text-slate-500">Actual Weight</span>
                      <input
                        type="number"
                        step="0.001"
                        value={formData.after_weight}
                        onChange={(e) => setFormData((prev) => ({ ...prev, after_weight: e.target.value }))}
                        className="w-32 px-2 py-1 border border-slate-300 rounded-md text-right font-mono text-slate-800"
                      />
                    </label>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500">Variance</span>
                      <span className={`font-bold ${isVarianceHigh ? 'text-red-700' : isVarianceWarn ? 'text-amber-700' : 'text-emerald-700'}`}>
                        {currentVariancePct.toFixed(2)}%
                      </span>
                    </div>
                  </div>
                  <div className={`mt-2 text-[11px] font-semibold ${isVarianceHigh ? 'text-red-700' : isVarianceWarn ? 'text-amber-700' : 'text-emerald-700'}`}>
                    {isVarianceHigh ? 'High variance - acknowledgment required' : isVarianceWarn ? 'Variance detected' : 'Within spec'}
                  </div>
                  {isVarianceHigh && (
                    <label className="mt-2 flex items-start gap-2 text-[11px] text-red-700 font-medium">
                      <input
                        type="checkbox"
                        checked={varianceAcknowledged}
                        onChange={(e) => setVarianceAcknowledged(e.target.checked)}
                        className="mt-0.5"
                      />
                      I acknowledge the weight variance and have verified manually.
                    </label>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => { setActiveAction('APPROVE'); setShowReworkConfirm(false); }}
                    disabled={failedRequiredCount > 0 || (isVarianceHigh && !varianceAcknowledged)}
                    title={
                      failedRequiredCount > 0
                        ? `Cannot fully approve: ${failedRequiredCount} checklist item(s) failed`
                        : (isVarianceHigh && !varianceAcknowledged ? 'Acknowledge high weight variance to proceed' : '')
                    }
                    className={`py-3 rounded-lg text-sm font-bold border transition-all disabled:opacity-40 disabled:cursor-not-allowed ${activeAction === 'APPROVE' ? 'bg-green-600 text-white border-green-600 ring-2 ring-offset-1 ring-green-500' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => { setActiveAction('REJECT'); setShowReworkConfirm(false); }}
                    className={`py-3 rounded-lg text-sm font-bold border transition-all
    ${activeAction === 'REJECT'
                        ? 'bg-red-600 text-white border-red-600 ring-2 ring-offset-1 ring-red-500'
                        : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}
  `}
                  >
                    Reject / Conflict
                  </button>
                </div>
                {failedRequiredCount > 0 && (
                  <div className="text-xs text-amber-700 bg-amber-50 px-2 py-1.5 rounded border border-amber-200 flex items-center gap-1">
                    <AlertTriangle size={11} /> Resolve {failedRequiredCount} failed checklist item{failedRequiredCount !== 1 ? 's' : ''} first
                  </div>
                )}

                {activeAction && (
                  <div className="animate-in fade-in slide-in-from-top-2 pt-2 border-t border-slate-100">
                    {/* {activeAction === 'CONFLICT' && (
                      <div className="mb-3">
                        <label className="text-xs font-bold text-slate-600 uppercase mb-1 block">Conflict Type</label>
                        <select 
                          value={conflictType} 
                          onChange={(e) => setConflictType(e.target.value)} 
                          className="w-full p-2 border border-slate-300 rounded-md text-sm outline-none focus:border-purple-500"
                        >
                          <option>Weight Mismatch</option>
                          <option>Material Overuse</option>
                          <option>Inventory Error</option>
                          <option>System Data Error</option>
                        </select>
                      </div>
                    )} */}

                    <label className="text-xs font-bold text-slate-600 uppercase mb-1 block">
                      {activeAction === 'APPROVE' ? 'Remarks (Optional)' : 'Remarks (Mandatory)'}
                    </label>
                    <textarea
                      value={remarks}
                      onChange={(e) => setRemarks(e.target.value)}
                      className={`w-full p-3 border rounded-md text-sm h-24 resize-none outline-none focus:ring-1 ${activeAction === 'APPROVE' ? 'border-slate-300 focus:border-green-500 focus:ring-green-500' : 'border-red-300 focus:border-red-500 focus:ring-red-500'}`}
                      placeholder={activeAction === 'APPROVE' ? "Good quality..." : "Reason for rejection/conflict..."}
                    ></textarea>

                    {/* Rework confirmation dialog */}
                    {showReworkConfirm && formData.rejected_qty > 0 && (
                      <div className="mt-3 p-3 rounded-lg border-2 border-red-300 bg-red-50 animate-in fade-in">
                        <div className="text-sm font-bold text-red-700 mb-2">
                          Confirm: {formData.rejected_qty} piece{formData.rejected_qty !== 1 ? 's' : ''} will be sent to Rework queue
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={handleSubmit}
                            disabled={loading}
                            className="flex-1 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-bold"
                          >
                            Yes, Confirm
                          </button>
                          <button
                            onClick={() => setShowReworkConfirm(false)}
                            className="flex-1 py-2 rounded-lg bg-slate-200 hover:bg-slate-300 text-slate-700 text-sm font-bold"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}

                    <button
                      onClick={handleSubmit}
                      disabled={loading || (activeAction === 'APPROVE' && isVarianceHigh && !varianceAcknowledged) || (evidenceRequired && evidenceFiles.length === 0)}
                      className={`w-full py-3 mt-4 rounded-lg text-white font-bold shadow-md transition-transform active:scale-95 flex items-center justify-center gap-2 ${loading ? 'opacity-70 cursor-not-allowed' : ''} ${activeAction === 'APPROVE' ? 'bg-green-600 hover:bg-green-700' :
                        activeAction === 'REJECT' ? 'bg-red-600 hover:bg-red-700' :
                          'bg-purple-600 hover:bg-purple-700'
                        }`}
                    >
                      {loading && (
                        <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                      )}
                      {loading ? (photoUploadLoading ? 'Uploading Photos...' : 'Processing...') : (evidenceRequired && evidenceFiles.length === 0 ? 'Upload Evidence Photos' : `Confirm ${activeAction}`)}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {lightboxPhoto && (
        <div
          className="fixed inset-0 z-50 bg-slate-950/80 flex items-center justify-center p-6"
          onClick={() => setLightboxPhoto(null)}
        >
          <div className="max-w-4xl w-full" onClick={(event) => event.stopPropagation()}>
            <img
              src={toAbsolutePhotoUrl(lightboxPhoto.photo_url)}
              alt={lightboxPhoto.photo_type}
              className="w-full max-h-[80vh] object-contain rounded-2xl bg-white"
            />
            <div className="mt-3 text-center text-sm text-white">
              {lightboxPhoto.photo_type?.toUpperCase()} {lightboxPhoto.remarks ? `• ${lightboxPhoto.remarks}` : ''}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// --- QC REWORK DASHBOARD ---

const QCReworkDashboard = ({ onReview }) => {
  const [reworkData, setReworkData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [photoViewer, setPhotoViewer] = useState({ open: false, loading: false, photos: [], title: '' });

  useEffect(() => {
    fetchReworks();
  }, []);

  const fetchReworks = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await QCService.getReworks();
      setReworkData(data);
    } catch (err) {
      console.error('Failed to fetch reworks:', err);
      setError('Failed to load rework data');
    } finally {
      setLoading(false);
    }
  };

  const handleViewPhotos = async (row) => {
    if (!row?.rejection_inspection_id) return;
    setPhotoViewer({ open: true, loading: true, photos: [], title: `Inspection #${row.rejection_inspection_id}` });
    try {
      const inspection = await QCService.getInspectionDetail(row.rejection_inspection_id);
      setPhotoViewer({
        open: true,
        loading: false,
        photos: inspection?.photos || [],
        title: `${row.job_card_no} - ${row.stage_name || row.stage_id}`,
      });
    } catch (err) {
      console.error('Failed to fetch inspection photos:', err);
      setPhotoViewer({ open: true, loading: false, photos: [], title: 'No photos available' });
    }
  };

  return (
    <div className="flex flex-col h-full bg-slate-50/50">
      <div className="px-8 py-6 border-b border-slate-200 bg-white">
        <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
          <RefreshCcw className="text-orange-600" /> Rework Queue
        </h1>
        <p className="text-slate-500 mt-1">Review corrected items resubmitted by production</p>
        {error && <p className="text-sm text-red-500 mt-1">{error}</p>}
      </div>

      <div className="flex-1 p-8 overflow-y-auto">
        <div className="max-w-4xl mx-auto">
          <div className="flex justify-between items-center mb-6">
            <div></div>
            <button
              onClick={fetchReworks}
              className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200 flex items-center gap-2"
            >
              <RefreshCcw size={16} /> Refresh
            </button>
          </div>

          {loading ? (
            <div className="space-y-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="bg-white border border-slate-200 p-5 rounded-xl animate-pulse">
                  <div className="flex gap-4 items-center">
                    <div className="w-12 h-12 bg-slate-200 rounded-full"></div>
                    <div className="space-y-2">
                      <div className="h-4 bg-slate-200 rounded w-32"></div>
                      <div className="h-3 bg-slate-200 rounded w-24"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : reworkData.length > 0 ? (
            <div className="grid grid-cols-1 gap-4">
              {reworkData.map(job => (
                <div key={job.rework_id} className="bg-white border border-slate-200 p-5 rounded-xl flex items-center justify-between shadow-sm hover:shadow-md transition-all">
                  <div className="flex gap-4 items-center">
                    <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 font-bold border border-orange-200">
                      <RefreshCcw size={20} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold text-slate-800">{job.job_card_no}</h3>
                        <span className="text-xs bg-slate-100 px-2 py-0.5 rounded text-slate-500 font-medium">
                          Stage {job.stage_name || job.stage_id}
                        </span>

                      </div>
                      <p className="text-sm text-red-600 mt-1 flex items-center gap-1">
                        <AlertTriangle size={12} /> Priority: {job.priority}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        Requested: {new Date(job.requested_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-right min-w-[140px]">
                      <div className="text-[11px] font-bold uppercase text-slate-400">Est. Rework Cost</div>
                      <div className="text-lg font-bold text-red-600">{formatCurrency(job.estimated_rework_cost)}</div>
                    </div>
                    <button
                      onClick={() => onReview(job)}
                      className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold rounded-lg shadow-sm"
                    >
                      Review Rework
                    </button>
                    {job.rejection_inspection_id && (
                      <button
                        onClick={() => handleViewPhotos(job)}
                        className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-lg border border-slate-200"
                      >
                        View Photos
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400">
              No rework items in queue
            </div>
          )}
        </div>
      </div>
      {photoViewer.open && (
        <div className="fixed inset-0 z-50 bg-slate-900/70 flex items-center justify-center p-4">
          <div className="qc-modal-panel bg-white rounded-xl border border-slate-200 shadow-xl w-full max-w-3xl max-h-[85vh] overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-bold text-slate-800 text-sm">{photoViewer.title}</h3>
              <button
                onClick={() => setPhotoViewer({ open: false, loading: false, photos: [], title: '' })}
                className="text-slate-500 hover:text-slate-700 text-sm font-semibold"
              >
                Close
              </button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[70vh]">
              {photoViewer.loading ? (
                <div className="text-sm text-slate-400">Loading photos...</div>
              ) : photoViewer.photos.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {photoViewer.photos.map((photo) => (
                    <img
                      key={photo.id}
                      src={toAbsolutePhotoUrl(photo.photo_url)}
                      alt={`Inspection evidence ${photo.id}`}
                      className="w-full h-36 object-cover rounded-lg border border-slate-200"
                    />
                  ))}
                </div>
              ) : (
                <div className="text-sm text-slate-400">No evidence photos available.</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// --- QC REWORK REVIEW SCREEN ---

const QCReworkReviewScreen = ({ reworkItem, onBack, onDecision }) => {

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [remarks, setRemarks] = useState('');
  const [activeAction, setActiveAction] = useState(null);
  //const [reworkItem, setReworkItem] = useState(null);
  if (!reworkItem) {
    return (
      <div className="flex items-center justify-center h-full text-slate-400">
        Loading rework details...
      </div>
    );
  }

  // This would normally come from props, but we'll handle it here


  const handleSubmit = async () => {
    if (!activeAction) {
      alert("Please select an action");
      return;
    }



    if (!remarks) {
      alert("Remarks are mandatory.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const decisionData = {
        decision: activeAction,
        remarks: remarks
      };

      const result = await QCService.reviewRework(reworkItem.rework_id, decisionData);


      alert(`Rework ${activeAction.toLowerCase()} successful! Status: ${result.status}`);
      onDecision(activeAction, { ...decisionData, result });

    } catch (err) {
      console.error('Rework review error:', err);
      setError(err.response?.data?.error || 'Failed to submit rework review');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-slate-50/50 overflow-hidden">
      <div className="px-8 py-5 border-b border-slate-200 bg-white shadow-sm flex justify-between items-center">
        <div className="flex items-center gap-4">
          <button onClick={onBack} className="p-2 hover:bg-slate-100 rounded-full text-black transition-colors">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-xl font-bold text-black flex items-center gap-3">
              Rework Review
              {reworkItem && (
                <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded border border-red-200 flex items-center gap-1 font-bold">
                  <RefreshCcw size={12} /> Rework ID: {reworkItem.id}
                </span>
              )}
            </h1>
            {reworkItem && (
              <div className="text-xs text-black mt-1">
                {reworkItem.job_card_no} • Stage: {reworkItem.stage} • Priority: {reworkItem.priority}
              </div>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="mx-8 mt-4 p-3 bg-red-50 border border-red-100 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-8">
        <div className="max-w-6xl mx-auto grid grid-cols-3 gap-6">
          <div className="col-span-2 space-y-6">
            <div className="bg-red-50 rounded-xl border border-red-100 p-5">
              <h3 className="font-bold text-red-900 text-sm mb-3 flex items-center gap-2">
                <History size={16} /> Rework Information
              </h3>
              {reworkItem ? (
                <div className="bg-white p-3 rounded-lg border border-red-100 mb-3 shadow-sm">
                  <div className="text-xs text-black font-bold uppercase mb-1">Job Details</div>
                  <div className="text-sm text-red-700 font-medium">
                    Job Card: {reworkItem.job_card_no} • Stage: {reworkItem.stage}
                  </div>
                </div>
              ) : (
                <div className="bg-white p-3 rounded-lg border border-red-100 mb-3 shadow-sm">
                  <div className="text-sm text-red-700 font-medium">
                    Loading rework details...
                  </div>
                </div>
              )}
              <div className="text-sm text-black">
                This item requires re-inspection after correction by production team.
              </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="bg-slate-50 px-6 py-3 border-b border-slate-200">
                <h3 className="font-bold text-black text-sm flex items-center gap-2">
                  <ArrowLeftRight size={16} className="text-indigo-600" /> Inspection Notes
                </h3>
              </div>
              <div className="p-6">
                <label className="text-xs font-bold text-black uppercase block mb-2">Review Remarks</label>
                <textarea
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  className="w-full p-3 border border-slate-300 rounded-md text-sm h-32 resize-none outline-none focus:ring-1 focus:ring-indigo-500 text-black placeholder:text-black"
                  placeholder="Enter detailed inspection notes and observations..."
                ></textarea>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-slate-200 shadow-lg overflow-hidden">
              <div className="bg-slate-900 px-5 py-3 border-b border-slate-800">
                <h3 className="font-bold text-white text-sm flex items-center gap-2">
                  <Lock size={16} /> Rework Decision
                </h3>
              </div>
              <div className="p-5 space-y-4">
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => setActiveAction('APPROVE')}
                    className={`py-3 rounded-lg text-sm font-bold border transition-all ${activeAction === 'APPROVE' ? 'bg-green-600 text-white border-green-600 ring-2 ring-offset-1 ring-green-500' : 'bg-white text-black border-slate-200 hover:bg-slate-50'}`}
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => setActiveAction('REJECT_AGAIN')}
                    className={`py-3 rounded-lg text-sm font-bold border transition-all
    ${activeAction === 'REJECT_AGAIN'
                        ? 'bg-red-600 text-white border-red-600 ring-2 ring-offset-1 ring-red-500'
                        : 'bg-white text-black border-slate-200 hover:bg-slate-50'}
  `}
                  >
                    Reject Again
                  </button>

                </div>

                {activeAction && (
                  <div className="animate-in fade-in slide-in-from-top-2 pt-2 border-t border-slate-100">
                    <label className="text-xs font-bold text-black uppercase mb-1 block">Decision Remarks</label>
                    <textarea
                      value={remarks}
                      onChange={(e) => setRemarks(e.target.value)}
                      className="w-full p-3 border border-slate-300 rounded-md text-sm h-24 resize-none outline-none focus:ring-1 text-black placeholder:text-black"
                      placeholder="Enter comments..."
                    ></textarea>

                    <button
                      onClick={handleSubmit}
                      disabled={loading}
                      className={`w-full py-3 mt-4 rounded-lg text-white font-bold shadow-md transition-transform active:scale-95 ${loading ? 'opacity-70 cursor-not-allowed' : ''} ${activeAction === 'APPROVE' ? 'bg-green-600 hover:bg-green-700' :
                        activeAction === 'REJECT' ? 'bg-red-600 hover:bg-red-700' :
                          'bg-orange-600 hover:bg-orange-700'
                        }`}
                    >
                      {loading ? 'Processing...' : `Confirm ${activeAction.replace('_', ' ')}`}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- INVENTORY QC CONFLICT SCREEN ---

const InventoryQCConflictScreen = ({ onBack }) => {
  const { getOptions } = useMasterData('CONFLICT_REASON');
  const conflictReasonOptions = getOptions('CONFLICT_REASON');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [conflictData, setConflictData] = useState({
    stage_id: '',
    issue_type: 'Weight Mismatch',
    remarks: ''
  });

  const handleSubmit = async () => {
    if (!conflictData.stage_id) {
      alert("Please enter Stage ID");
      return;
    }

    if (!conflictData.remarks) {
      alert("Remarks are mandatory");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await QCService.createConflict(conflictData);

      alert(`Conflict reported successfully! ID: ${result.conflict_id}`);
      // Reset form
      setConflictData({
        stage_id: '',
        issue_type: 'Weight Mismatch',
        remarks: ''
      });

    } catch (err) {
      console.error('Conflict creation error:', err);
      setError(err.response?.data?.error || 'Failed to report conflict');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-slate-50/50 overflow-hidden">
      <div className="px-8 py-5 border-b border-slate-200 bg-white shadow-sm flex justify-between items-center">
        <div className="flex items-center gap-4">
          <button onClick={onBack} className="p-2 hover:bg-slate-100 rounded-full text-slate-500 transition-colors">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-xl font-bold text-slate-800 flex items-center gap-3">
              Report Inventory Conflict
            </h1>
            <div className="text-xs text-slate-500 mt-1">
              Report material or inventory discrepancies
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="mx-8 mt-4 p-3 bg-red-50 border border-red-100 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-8">
        <div className="max-w-5xl mx-auto space-y-6">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <h3 className="font-bold text-slate-800 mb-6 flex items-center gap-2">
              <AlertOctagon size={20} className="text-purple-600" /> Conflict Details
            </h3>

            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-500 uppercase">Stage ID *</label>
                <input
                  type="number"
                  value={conflictData.stage_id}
                  onChange={(e) => setConflictData({ ...conflictData, stage_id: e.target.value })}
                  className="w-full p-2 border border-slate-300 rounded-md text-sm"
                  placeholder="Enter stage ID"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-500 uppercase">Issue Type *</label>
                <select
                  value={conflictData.issue_type}
                  onChange={(e) => setConflictData({ ...conflictData, issue_type: e.target.value })}
                  className="w-full p-2 border border-slate-300 rounded-md text-sm"
                >
                  {conflictReasonOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label || option.value}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-span-2 space-y-1">
                <label className="text-xs font-bold text-slate-500 uppercase">Detailed Remarks *</label>
                <textarea
                  value={conflictData.remarks}
                  onChange={(e) => setConflictData({ ...conflictData, remarks: e.target.value })}
                  className="w-full p-2 border border-slate-300 rounded-md text-sm h-32 resize-none"
                  placeholder="Describe the conflict in detail..."
                />
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-slate-100 text-right">
              <button
                onClick={handleSubmit}
                disabled={loading || !conflictData.stage_id || !conflictData.remarks}
                className={`px-6 py-2.5 bg-purple-600 text-white rounded-lg text-sm font-bold shadow-sm hover:bg-purple-700 transition-colors ${loading || !conflictData.stage_id || !conflictData.remarks ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
              >
                {loading ? 'Submitting...' : 'Report Conflict'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- FINAL QC LIST SCREEN ---

const FinalQCList = ({ onReview }) => {
  const [finalQCData, setFinalQCData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchFinalQCQueue();
  }, []);

  const fetchFinalQCQueue = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await QCService.getFinalQCQueue();
      setFinalQCData(data);
    } catch (err) {
      console.error('Failed to fetch final QC queue:', err);
      setError('Failed to load final QC queue');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-slate-50/50">
      <div className="px-8 py-6 border-b border-slate-200 bg-white">
        <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
          <ShieldCheck className="text-emerald-600" />
          Final QC Approval Queue
        </h1>
        <p className="text-slate-500 mt-1">Jobs with all production stages completed, awaiting final sign-off.</p>
        {error && <p className="text-sm text-red-500 mt-1">{error}</p>}
      </div>

      <div className="flex-1 p-8 overflow-y-auto">
        <div className="max-w-4xl mx-auto">
          <div className="flex justify-between items-center mb-6">
            <div></div>
            <button
              onClick={fetchFinalQCQueue}
              className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200 flex items-center gap-2"
            >
              <RefreshCcw size={16} /> Refresh
            </button>
          </div>

          {loading ? (
            <div className="space-y-4">
              {Array.from({ length: 2 }).map((_, i) => (
                <div key={i} className="bg-white border border-slate-200 p-5 rounded-xl animate-pulse">
                  <div className="flex gap-4 items-center">
                    <div className="w-12 h-12 bg-slate-200 rounded-full"></div>
                    <div className="space-y-2">
                      <div className="h-4 bg-slate-200 rounded w-32"></div>
                      <div className="h-3 bg-slate-200 rounded w-24"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : finalQCData.length > 0 ? (
            <div className="grid grid-cols-1 gap-4">
              {finalQCData.map(job => (
                <div key={job.job_card_id} className="bg-white border border-slate-200 p-5 rounded-xl flex items-center justify-between shadow-sm hover:shadow-md transition-all">
                  <div className="flex gap-4 items-center">
                    <div className="w-12 h-12 bg-emerald-50 rounded-full flex items-center justify-center text-emerald-600 font-bold border border-emerald-100">
                      <CheckCircle2 size={20} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold text-slate-800 text-lg">{job.job_card_no}</h3>
                        <span className="text-xs bg-slate-100 px-2 py-0.5 rounded text-slate-500 font-medium">{job.design_code}</span>
                      </div>
                      <p className="text-sm text-slate-500 mt-1">
                        Priority: {job.priority === 1 ? 'High' : 'Normal'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => onReview(job)}
                    className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold rounded-lg shadow-sm flex items-center gap-2"
                  >
                    Perform Final Review <ArrowRight size={16} />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400">
              No jobs ready for final QC
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// --- FINAL QC APPROVAL SCREEN ---

const FinalQCApprovalScreen = ({ jobItem: jobItemProp, onBack, onDecision }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [finalQCData, setFinalQCData] = useState({
    approved: true,
    remarks: ''
  });
  const jobItem = jobItemProp || {
    job_card_id: 0,
    job_card_no: 'Loading...',
    design_code: 'Loading...'
  };

  const handleSubmit = async () => {
    if (!finalQCData.remarks && !finalQCData.approved) {
      alert("Remarks are mandatory for rejection");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await QCService.submitFinalQC(jobItem.job_card_id, finalQCData);

      alert(`Final QC ${finalQCData.approved ? 'approved' : 'rejected'} successfully! Status: ${result.status}`);
      onDecision(finalQCData.approved ? 'APPROVE' : 'REJECT', { ...finalQCData, result });

    } catch (err) {
      console.error('Final QC submission error:', err);
      setError(err.response?.data?.error || 'Failed to submit final QC');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-slate-50/50 overflow-hidden">
      <div className="px-8 py-5 border-b border-slate-200 bg-white shadow-sm flex justify-between items-center">
        <div className="flex items-center gap-4">
          <button onClick={onBack} className="p-2 hover:bg-slate-100 rounded-full text-slate-500 transition-colors">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-xl font-bold text-slate-800 flex items-center gap-3">
              Final QC Approval
              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded border border-blue-200 font-bold">
                {jobItem.job_card_no}
              </span>
            </h1>
            <div className="text-xs text-slate-500 mt-1">
              {jobItem.design_code} • Final Quality Inspection
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="mx-8 mt-4 p-3 bg-red-50 border border-red-100 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-8">
        <div className="max-w-6xl mx-auto space-y-6">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <h3 className="font-bold text-slate-800 mb-6 flex items-center gap-2">
              <ShieldCheck size={20} className="text-emerald-600" /> Final Inspection Decision
            </h3>

            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="approval"
                    checked={finalQCData.approved}
                    onChange={() => setFinalQCData({ ...finalQCData, approved: true })}
                    className="w-4 h-4 text-emerald-600"
                  />
                  <span className="text-sm font-medium text-slate-700">Approve - Job meets all quality standards</span>
                </label>

                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="approval"
                    checked={!finalQCData.approved}
                    onChange={() => setFinalQCData({ ...finalQCData, approved: false })}
                    className="w-4 h-4 text-red-600"
                  />
                  <span className="text-sm font-medium text-slate-700">Reject - Job does not meet quality standards</span>
                </label>
              </div>

              <div>
                <label className="text-xs font-bold text-slate-500 uppercase block mb-2">
                  {finalQCData.approved ? 'Approval Remarks (Optional)' : 'Rejection Reasons (Required)'}
                </label>
                <textarea
                  value={finalQCData.remarks}
                  onChange={(e) => setFinalQCData({ ...finalQCData, remarks: e.target.value })}
                  className={`w-full p-3 border rounded-md text-sm h-32 resize-none outline-none focus:ring-1 ${finalQCData.approved ? 'border-slate-300 focus:border-emerald-500' : 'border-red-300 focus:border-red-500'
                    }`}
                  placeholder={finalQCData.approved ? "Add any additional comments..." : "Explain rejection reasons in detail..."}
                />
              </div>

              <div className="pt-4 border-t border-slate-100 text-right">
                <button
                  onClick={handleSubmit}
                  disabled={loading || (!finalQCData.approved && !finalQCData.remarks)}
                  className={`px-6 py-2.5 rounded-lg text-white font-bold shadow-md transition-colors ${loading ? 'opacity-70 cursor-not-allowed' : ''
                    } ${finalQCData.approved ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'}`}
                >
                  {loading ? 'Processing...' : `Confirm ${finalQCData.approved ? 'Approval' : 'Rejection'}`}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- QC HISTORY & AUDIT SCREEN ---

const QCHistoryScreen = () => {
  const [filter, setFilter] = useState('');
  const [expandedRow, setExpandedRow] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);


  const fetchHistory = useCallback(async ({ reset = false, search = filter } = {}) => {
    try {
      reset ? setLoading(true) : setLoadingMore(true);

      const res = await QCService.getHistory({
        page: reset ? 1 : page,
        limit: 100,
        search
      });

      setHistoryData(prev =>
        reset ? res.data : [...prev, ...res.data]
      );

      setHasNext(res.has_next);
      setPage(prev => reset ? 2 : prev + 1);

    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [filter, page]);

  useEffect(() => {
    fetchHistory({ reset: true });
  }, [fetchHistory]);



  // const filteredHistory = historyData.filter(item => 
  //   item.job_card_no.toLowerCase().includes(filter.toLowerCase()) || 
  //   item.qc_user.toLowerCase().includes(filter.toLowerCase())
  // );

  const toggleRow = (id) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  return (
    <div className="flex flex-col h-full bg-slate-50/50 overflow-hidden">
      <div className="px-8 py-5 border-b border-slate-200 bg-white shadow-sm flex justify-between items-center">
        <div>
          <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <History className="text-indigo-600" /> QC History & Audit Log
          </h1>
          <p className="text-xs text-slate-500 mt-1">Immutable record of all quality control actions</p>
        </div>
        <div className="flex gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 text-slate-400" size={16} />
            <input
              type="text"
              placeholder="Search Log..."
              className="pl-9 pr-4 py-2 border border-slate-300 rounded-lg text-sm w-64"
              value={filter}
              onChange={(e) => {
                const value = e.target.value;
                setFilter(value);
                fetchHistory({ reset: true, search: value });
              }}

            />
          </div>
          <button
            onClick={() => QCService.exportHistory()}
            className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200"
          >
            <Download size={16} /> Export Report
          </button>

        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-8">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : historyData.length > 0 ? (
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            <table className="w-full text-sm text-left">
              <thead className="bg-slate-50 text-slate-500 uppercase font-semibold text-xs border-b">
                <tr>
                  <th className="px-6 py-3">Timestamp</th>
                  <th className="px-6 py-3">Job / Stage</th>
                  <th className="px-6 py-3">Decision</th>
                  <th className="px-6 py-3">Inspector</th>
                  <th className="px-6 py-3">Remarks</th>
                  <th className="px-6 py-3 text-right">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {historyData.map((row) => (
                  <React.Fragment key={row.id}>
                    <tr
                      className="hover:bg-slate-50 cursor-pointer"
                      onClick={() => toggleRow(row.id)}
                    >
                      <td className="px-6 py-4 font-mono text-slate-600 text-xs">
                        {new Date(row.timestamp).toLocaleString()}
                      </td>

                      <td className="px-6 py-4">
                        <div className="font-bold text-slate-800">{row.job_card_no}</div>
                        <div className="text-xs text-slate-500">
                          {row.stage_name} ({row.stage_type})
                        </div>
                      </td>

                      <td className="px-6 py-4">
                        <span
                          className={`px-2 py-1 rounded text-[10px] font-bold uppercase ${row.decision === 'APPROVED'
                            ? 'bg-green-100 text-green-700'
                            : row.decision === 'REJECTED'
                              ? 'bg-red-100 text-red-700'
                              : row.decision === 'CONFLICT'
                                ? 'bg-purple-100 text-purple-700'
                                : 'bg-slate-100 text-slate-700'
                            }`}
                        >
                          {row.decision.replace(/_/g, ' ')}
                        </span>


                      </td>

                      <td className="px-6 py-4">
                        {row.is_bypass ? (
                          <span className="inline-flex items-center rounded-full bg-blue-50 text-blue-700 border border-blue-100 px-2.5 py-1 text-xs font-bold">
                            Auto-Approved (Bypass)
                          </span>
                        ) : (
                          <div className="flex items-center gap-2 text-black">
                            <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-[10px] text-slate-600 font-bold">
                              {row.inspector?.charAt(0)}
                            </div>
                            {row.inspector}
                          </div>
                        )}
                      </td>

                      <td className="px-6 py-4 text-slate-600 truncate max-w-xs">
                        {row.remarks}
                      </td>

                      <td className="px-6 py-4 text-right">
                        <ChevronDown
                          size={16}
                          className={`text-slate-400 transition-transform ${expandedRow === row.id ? 'rotate-180' : ''
                            }`}
                        />
                      </td>
                    </tr>

                    {expandedRow === row.id && (
                      <tr className="bg-slate-50/50">
                        <td colSpan="6" className="px-6 py-4 border-t border-b border-slate-100">
                          <div className="grid grid-cols-4 gap-4 text-xs">
                            <div className="p-3 bg-white border rounded-lg">
                              <span className="text-slate-400 font-bold block mb-1">
                                Rejected Pieces
                              </span>
                              <span className="text-lg font-mono text-slate-800">
                                {row.rejected_qty || 0}
                              </span>
                            </div>

                            <div className="p-3 bg-white border rounded-lg">
                              <span className="text-slate-400 font-bold block mb-1">
                                Rework Entries
                              </span>
                              <span className="text-lg font-mono text-slate-800">
                                {row.rework_count || 0}
                              </span>
                            </div>

                            <div className="col-span-2 p-3 bg-white border rounded-lg">
                              <span className="text-slate-400 font-bold block mb-1">
                                Full Remarks
                              </span>
                              <p className="text-slate-700 leading-relaxed">
                                {row.remarks}
                              </p>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>

            </table>
            {hasNext && (
              <div className="flex justify-center py-6">
                <button
                  onClick={() => fetchHistory()}
                  disabled={loadingMore}
                  className="px-6 py-2 rounded-lg bg-slate-100 text-slate-700 text-sm font-semibold hover:bg-slate-200 disabled:opacity-50"
                >
                  {loadingMore ? 'Loading...' : 'Load More'}
                </button>
              </div>
            )}

          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-8 text-center text-slate-400">
            No history data available
          </div>
        )}
      </div>
    </div>
  );
};

// --- QC ANALYTICS SCREEN ---

const QCAnalyticsScreen = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('by_stage');

  useEffect(() => {
    QCService.getDefectAnalytics()
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const BAR_COLORS = ['bg-indigo-500', 'bg-blue-500', 'bg-purple-500', 'bg-cyan-500', 'bg-violet-500', 'bg-sky-500'];

  const renderBars = (rows, labelKey, countKey) => {
    if (!rows || rows.length === 0) return (
      <div className="text-center py-8 text-slate-400">No defect data in last 30 days</div>
    );
    const max = Math.max(...rows.map(r => r[countKey]), 1);
    return (
      <div className="space-y-3">
        {rows.map((row, i) => (
          <div key={i} className="flex items-center gap-3 text-sm">
            <div className="w-36 text-slate-600 font-medium truncate text-right text-xs">{row[labelKey]}</div>
            <div className="flex-1 h-6 bg-slate-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full ${BAR_COLORS[i % BAR_COLORS.length]} flex items-center pl-2`}
                style={{ width: `${Math.max((row[countKey] / max) * 100, 8)}%` }}
              >
                <span className="text-white text-[10px] font-bold">{row[countKey]}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full bg-slate-50/50 overflow-hidden">
      <div className="px-8 py-5 border-b border-slate-200 bg-white shadow-sm">
        <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
          <BarChart3 className="text-indigo-600" /> Defect Analytics
        </h1>
        <p className="text-xs text-slate-500 mt-1">Defect trends over last 30 days</p>
      </div>

      <div className="flex-1 overflow-y-auto p-8">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : (
          <div className="max-w-5xl mx-auto space-y-6">
            {/* Summary cards */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm text-center">
                <div className="text-3xl font-bold text-indigo-600">
                  {data?.by_stage?.reduce((s, r) => s + r.count, 0) ?? 0}
                </div>
                <div className="text-xs text-slate-500 mt-1 font-medium uppercase">Total Defects (30d)</div>
              </div>
              <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm text-center">
                <div className="text-3xl font-bold text-red-600">{data?.by_stage?.length ?? 0}</div>
                <div className="text-xs text-slate-500 mt-1 font-medium uppercase">Affected Stages</div>
              </div>
              <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm text-center">
                <div className="text-3xl font-bold text-amber-600">{data?.by_category?.length ?? 0}</div>
                <div className="text-xs text-slate-500 mt-1 font-medium uppercase">Defect Categories</div>
              </div>
            </div>

            {/* Tabs */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="flex border-b border-slate-200">
                {[
                  { key: 'by_stage', label: 'By Stage' },
                  { key: 'by_category', label: 'By Category' },
                  { key: 'by_worker', label: 'By Inspector' }
                ].map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`px-6 py-3 text-sm font-bold border-b-2 transition-colors ${activeTab === tab.key ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              <div className="p-6">
                {activeTab === 'by_stage' && renderBars(data?.by_stage, 'stage', 'count')}
                {activeTab === 'by_category' && renderBars(data?.by_category, 'category', 'count')}
                {activeTab === 'by_worker' && renderBars(data?.by_worker, 'worker', 'count')}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


// --- RE-INSPECTION QUEUE SCREEN ---
const ReinspectionQueueScreen = ({ onReview }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchQueue();
  }, []);

  const fetchQueue = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await QCService.getReinspectionQueue();
      setItems(res.data || []);
    } catch (err) {
      console.error('Failed to fetch reinspection queue:', err);
      setError('Failed to load reinspection queue');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-slate-50/50">
      <div className="px-8 py-6 border-b border-slate-200 bg-white">
        <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
          <CheckSquare className="text-blue-600" /> Re-Inspection Queue
        </h1>
        <p className="text-slate-500 mt-1">Reworked items ready for QC re-inspection</p>
        {error && <p className="text-sm text-red-500 mt-1">{error}</p>}
      </div>

      <div className="flex-1 p-8 overflow-y-auto">
        <div className="max-w-4xl mx-auto">
          <div className="flex justify-between items-center mb-6">
            <span className="text-sm text-black">{items.length} item{items.length !== 1 ? 's' : ''} awaiting re-inspection</span>
            <button
              onClick={fetchQueue}
              className="px-4 py-2 bg-slate-100 text-black rounded-lg text-sm font-medium hover:bg-slate-200 flex items-center gap-2"
            >
              <RefreshCcw size={16} /> Refresh
            </button>
          </div>

          {loading ? (
            <div className="space-y-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="bg-white border border-slate-200 p-5 rounded-xl animate-pulse">
                  <div className="flex gap-4 items-center">
                    <div className="w-12 h-12 bg-slate-200 rounded-full"></div>
                    <div className="space-y-2">
                      <div className="h-4 bg-slate-200 rounded w-32"></div>
                      <div className="h-3 bg-slate-200 rounded w-24"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : items.length > 0 ? (
            <div className="grid grid-cols-1 gap-4">
              {items.map(item => (
                <div key={item.rework_id} className="bg-white border border-slate-200 p-5 rounded-xl flex items-center justify-between shadow-sm hover:shadow-md transition-all">
                  <div className="flex gap-4 items-center">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold border border-blue-200">
                      <CheckSquare size={20} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold text-black">{item.job_card_no || `JC-${item.job_card_id}`}</h3>
                        <span className="text-xs bg-slate-100 px-2 py-0.5 rounded text-black font-medium">
                          {item.stage_name || item.stage_code || `Stage ${item.stage_id}`}
                        </span>
                        <span className="text-xs bg-blue-100 px-2 py-0.5 rounded text-blue-700 font-medium">
                          REWORK DONE
                        </span>
                      </div>
                       <p className="text-sm text-black mt-1">
                        {item.defect_type && <span className="text-red-600">{item.defect_type}</span>}
                        {item.pieces_to_rework && <span className="ml-2 text-black">({item.pieces_to_rework} pcs)</span>}
                      </p>
                      <p className="text-xs text-black mt-1">
                        {item.design_code && <span>{item.design_code} - {item.design_name}</span>}
                        {item.completed_at && <span className="ml-3">Completed: {new Date(item.completed_at).toLocaleString()}</span>}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => onReview({
                      rework_id: item.rework_id,
                      stage_id: item.stage_id,
                      job_card_id: item.job_card_id,
                      job_card_no: item.job_card_no,
                      is_reinspection: true,
                    })}
                    className="px-5 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 flex items-center gap-2 shadow-sm"
                  >
                    Re-Inspect <ArrowRight size={16} />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 text-black">
              <CheckCircle2 size={48} className="mx-auto mb-4 text-emerald-300" />
              <p className="text-lg font-medium">No items awaiting re-inspection</p>
              <p className="text-sm mt-1">All reworked items have been inspected</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


// --- APP SHELL ---

export default function QCDashboardMain({ user, onLogout }) {
  const [activeScreen, setActiveScreen] = useState('QC_DASHBOARD');
  const [selectedQCItem, setSelectedQCItem] = useState(null);
  const [selectedReworkItem, setSelectedReworkItem] = useState(null);
  const [selectedFinalQCItem, setSelectedFinalQCItem] = useState(null);

  const handleLogout = useCallback(() => {
    setAuthToken(null);
    onLogout();
  }, [onLogout]);
  const currentUser = user || getStoredUser() || {};
  const localRole = getStoredRole() || currentUser.role || '';
  const normalizedRole = String(localRole || '').trim().toUpperCase().replace(/[\s-]+/g, '_');
  const isWorkerOnly = normalizedRole === 'PRODUCTION_WORKER';
  const isInspector = normalizedRole === 'QC_INSPECTOR';

  const visibleNav = isWorkerOnly ? {
    QC_REWORK_LIST: true,
  } : isInspector ? {
    QC_QUEUE: true,
    REINSPECTION_QUEUE: true,
    QC_HISTORY: true,
    QC_GOALS: true,
  } : {
    QC_DASHBOARD: true,
    QC_QUEUE: true,
    QC_REWORK_LIST: true,
    REINSPECTION_QUEUE: true,
    QC_CONFLICT: true,
    FINAL_QC_LIST: true,
    QC_ANALYTICS: true,
    QC_HISTORY: true,
    QC_GOALS: true,
  };

  useEffect(() => {
    if (isWorkerOnly && activeScreen !== 'QC_REWORK_LIST') setActiveScreen('QC_REWORK_LIST');
    if (isInspector && !['QC_QUEUE', 'REINSPECTION_QUEUE', 'QC_HISTORY', 'QC_REVIEW', 'QC_REWORK_REVIEW'].includes(activeScreen)) {
      setActiveScreen('QC_QUEUE');
    }
  }, [activeScreen, isInspector, isWorkerOnly]);


  const handleNavigate = async (screen, item = null) => {
    // Fresh QC review (stage QC)
    if (screen === 'QC_REVIEW' && item?.stage_id) {
      try {
        const fullData = await QCService.getStageReview(item.stage_id);
        setSelectedQCItem(fullData);
      } catch (err) {
        alert('Failed to load QC review data');
        return;
      }
    }

    // ✅ Rework QC review
    if (screen === 'QC_REWORK_REVIEW' && item?.rework_id) {
      setSelectedReworkItem(item);
    }

    // ✅ Final QC review
    if (screen === 'FINAL_QC_REVIEW' && item) {
      setSelectedFinalQCItem(item);
    }

    setActiveScreen(screen);
  };



  const handleQCDecision = (decision, data) => {
    console.log("QC Decision:", decision, data);
    const defectiveQty = Number(data?.result?.defective_qty || data?.rejected_qty || 0);
    const successMessage = decision === 'APPROVE' && defectiveQty > 0
      ? `Stage approved. ${defectiveQty} piece(s) sent to rework queue. Next stage unlocked.`
      : `Stage ${decision.toLowerCase()} successful!`;
    alert(successMessage);
    setActiveScreen('QC_QUEUE');
  };

  const handleFinalQCDecision = (decision, data) => {
    console.log("Final QC Decision:", decision, data);
    alert(`Final QC ${decision === 'APPROVE' ? 'Approved' : 'Rejected'}`);
    setActiveScreen('QC_DASHBOARD');
  };

  return (
    <div className="qc-root flex h-screen bg-[#111827] font-sans text-[#e6eef7] overflow-hidden">
      <style>{`
        .qc-skeleton {
          background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
          background-size: 200% 100%;
          animation: shimmer 1.5s infinite;
        }
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        .qc-modal { display: flex; flex-direction: column; max-height: 90vh; }
        .qc-modal-header { flex-shrink: 0; position: sticky; top: 0; z-index: 20; }
        .qc-modal-body { flex: 1; overflow-y: auto; }
        .qc-modal-footer { flex-shrink: 0; }
        .qc-main-content .text-slate-300,
        .qc-main-content .text-slate-400,
        .qc-main-content .text-slate-500,
        .qc-main-content .text-slate-600,
        .qc-main-content .text-slate-700,
        .qc-main-content .text-slate-800 {
          color: #000000 !important;
        }
        @media (max-width: 768px) {
          .qc-root table {
            display: block;
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
            white-space: nowrap;
          }
          .qc-root button {
            min-height: 44px;
            font-size: 16px;
          }
          .qc-modal {
            width: 100vw !important;
            height: 100vh !important;
            max-height: 100vh !important;
          }
          .qc-modal-header { padding: 16px !important; border-bottom: 1px solid #e5e7eb !important; }
          .qc-modal-body { padding: 16px !important; }
          .qc-modal-footer {
            position: sticky;
            bottom: 0;
            padding: 16px !important;
            border-top: 1px solid #e5e7eb !important;
            z-index: 20;
            background: #ffffff;
          }
          .qc-modal-panel {
            width: 100% !important;
            height: 100vh !important;
            max-height: 100vh !important;
            margin: 0 !important;
            border-radius: 0 !important;
          }
        }
      `}</style>
      {/* QC SIDEBAR */}
      <aside className="w-64 bg-slate-900 text-white flex flex-col shadow-2xl z-20">
        <div className="p-6 border-b border-slate-800 flex items-center gap-3">
          <div className="w-10 h-10 bg-[#3B82F6] rounded-xl flex items-center justify-center shadow-lg">
            <Gem className="text-white" size={24} />
          </div>
          <div>
            <h1 className="text-lg font-bold text-[#e6eef7]">JewelFlow</h1>
            <div className="text-xs text-[#e6eef7]">QC Module</div>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          <div className="text-[10px] font-bold text-slate-500 uppercase px-3 mt-4 mb-2">Main</div>

          {visibleNav.QC_DASHBOARD && (
            <button
              onClick={() => handleNavigate('QC_DASHBOARD')}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${activeScreen === 'QC_DASHBOARD' ? 'bg-[#3B82F6] text-white shadow-lg' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}
            >
              <LayoutDashboard size={18} />
              <span className="font-medium text-sm">QC Dashboard</span>
            </button>
          )}

          {visibleNav.QC_QUEUE && (
            <button
              onClick={() => handleNavigate('QC_QUEUE')}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${activeScreen === 'QC_QUEUE' ? 'bg-[#3B82F6] text-white shadow-lg' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}
            >
              <List size={18} />
              <span className="font-medium text-sm">QC Queue</span>
            </button>
          )}

          <div className="text-[10px] font-bold text-slate-500 uppercase px-3 mt-6 mb-2">Actions</div>

          {visibleNav.QC_REWORK_LIST && (
            <button
              onClick={() => handleNavigate('QC_REWORK_LIST')}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${activeScreen === 'QC_REWORK_LIST' || activeScreen === 'QC_REWORK_REVIEW' ? 'bg-[#3B82F6] text-white shadow-lg' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}
            >
              <RefreshCcw size={18} />
              <span className="font-medium text-sm">Rework Queue</span>
            </button>
          )}

          {visibleNav.REINSPECTION_QUEUE && (
            <button
              onClick={() => handleNavigate('REINSPECTION_QUEUE')}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${activeScreen === 'REINSPECTION_QUEUE' ? 'bg-[#3B82F6] text-white shadow-lg' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}
            >
              <CheckSquare size={18} />
              <span className="font-medium text-sm">Re-Inspection</span>
            </button>
          )}

          {visibleNav.QC_CONFLICT && (
            <button
              onClick={() => handleNavigate('QC_CONFLICT')}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${activeScreen === 'QC_CONFLICT' ? 'bg-[#3B82F6] text-white shadow-lg' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}
            >
              <AlertOctagon size={18} />
              <span className="font-medium text-sm">Inv-QC Conflicts</span>
            </button>
          )}

          {visibleNav.FINAL_QC_LIST && (
            <button
              onClick={() => handleNavigate('FINAL_QC_LIST')}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${activeScreen === 'FINAL_QC_LIST' || activeScreen === 'FINAL_QC_REVIEW' ? 'bg-[#3B82F6] text-white shadow-lg' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}
            >
              <ShieldCheck size={18} />
              <span className="font-medium text-sm">Final Approval</span>
            </button>
          )}

          <div className="text-[10px] font-bold text-slate-500 uppercase px-3 mt-6 mb-2">Reports</div>

          {visibleNav.QC_ANALYTICS && (
            <button
              onClick={() => handleNavigate('QC_ANALYTICS')}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${activeScreen === 'QC_ANALYTICS' ? 'bg-[#3B82F6] text-white shadow-lg' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}
            >
              <BarChart3 size={18} />
              <span className="font-medium text-sm">Defect Analytics</span>
            </button>
          )}

          {visibleNav.QC_HISTORY && (
            <button
              onClick={() => handleNavigate('QC_HISTORY')}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${activeScreen === 'QC_HISTORY' ? 'bg-[#3B82F6] text-white shadow-lg' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}
            >
              <History size={18} />
              <span className="font-medium text-sm">QC History</span>
            </button>
          )}

          {visibleNav.QC_GOALS && (
            <button
              onClick={() => handleNavigate('QC_GOALS')}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${activeScreen === 'QC_GOALS' ? 'bg-[#3B82F6] text-white shadow-lg' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}
            >
              <CheckSquare size={18} />
              <span className="font-medium text-sm">Goals & Tasks</span>
            </button>
          )}
        </nav>

        <div className="p-4 m-4 bg-slate-800 rounded-xl border border-slate-700">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-emerald-900 flex items-center justify-center text-xs font-bold text-emerald-400 border border-emerald-700">
              QC
            </div>
            <div>
              <div className="text-sm font-medium text-white">{currentUser.first_name || currentUser.username || 'QC User'} {currentUser.last_name || ''}</div>
              <div className="text-[10px] text-slate-400">{currentUser.role || 'QC Module'}</div>
            </div>
          </div>
        </div>
      </aside>

      {/* MAIN CONTENT */}
      <main className="qc-main-content flex-1 flex flex-col h-screen overflow-hidden bg-slate-50">
        {/* Top Header */}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-8 shadow-sm z-10">
          <div className="flex items-center gap-4 text-slate-500 text-sm">
            <span className="hover:text-emerald-600 cursor-pointer transition-colors">Home</span>
            <span className="text-slate-300">/</span>
            <span className="font-medium text-slate-800">
              {activeScreen.replace(/_/g, ' ')}
            </span>
          </div>

          <div className="flex items-center">
            <button
              onClick={handleLogout}
              className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-lg text-sm font-semibold border border-red-100 hover:bg-red-100 transition-colors"
            >
              <LogOut size={16} />
              Logout
            </button>
          </div>
        </header>

        {/* Dynamic Content */}
        <div className="flex-1 overflow-hidden relative">
          {activeScreen === 'QC_DASHBOARD' && <QCDashboard onNavigate={handleNavigate} user={currentUser} />}
          {activeScreen === 'QC_QUEUE' && <QCQueueScreen onReview={(item) => handleNavigate('QC_REVIEW', item)} />}
          {activeScreen === 'QC_REVIEW' && <QCReviewScreen qcItem={selectedQCItem} onBack={() => handleNavigate('QC_QUEUE')} onDecision={handleQCDecision} />}
          {activeScreen === 'QC_REWORK_LIST' && <QCReworkDashboard onReview={(item) => handleNavigate('QC_REWORK_REVIEW', item)} />}
          {activeScreen === 'QC_REWORK_REVIEW' && (
            <QCReworkReviewScreen
              reworkItem={selectedReworkItem}
              onBack={() => handleNavigate('QC_REWORK_LIST')}
              onDecision={handleQCDecision}
            />
          )}

          {activeScreen === 'REINSPECTION_QUEUE' && <ReinspectionQueueScreen onReview={(item) => handleNavigate('QC_REWORK_REVIEW', item)} />}
          {activeScreen === 'QC_CONFLICT' && <InventoryQCConflictScreen onBack={() => handleNavigate('QC_DASHBOARD')} />}
          {activeScreen === 'FINAL_QC_LIST' && <FinalQCList onReview={(item) => handleNavigate('FINAL_QC_REVIEW', item)} />}
          {activeScreen === 'FINAL_QC_REVIEW' && <FinalQCApprovalScreen jobItem={selectedFinalQCItem} onBack={() => handleNavigate('FINAL_QC_LIST')} onDecision={handleFinalQCDecision} />}
          {activeScreen === 'QC_HISTORY' && <QCHistoryScreen />}
          {activeScreen === 'QC_ANALYTICS' && <QCAnalyticsScreen />}
          {activeScreen === 'QC_GOALS' && <div className="h-full overflow-y-auto p-6"><ModuleGoalsDashboard moduleName="QC" /></div>}

          {!['QC_DASHBOARD', 'QC_QUEUE', 'QC_REVIEW', 'QC_REWORK_LIST', 'QC_REWORK_REVIEW', 'REINSPECTION_QUEUE', 'QC_CONFLICT', 'FINAL_QC_LIST', 'FINAL_QC_REVIEW', 'QC_HISTORY', 'QC_ANALYTICS', 'QC_GOALS'].includes(activeScreen) && (
            <div className="flex flex-col items-center justify-center h-full text-slate-400">
              <LayoutDashboard size={40} className="mb-4 text-slate-300" />
              <p>Select "QC Dashboard" to view the implemented screen.</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
